import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Search, Trash2, ChevronDown, ChevronUp, RefreshCw, Plus, Save, X, Ban, Copy, Star,
  AlertTriangle, Pencil, Check, Stethoscope, Users, Scissors, FlaskConical,
  //  Aliased: `Pill` is also the name of this file's own choice-chip component, and the
  //  collision is a parse error rather than a shadowing warning.
  Pill as PillIcon,
} from 'lucide-react';

import {
  conditionTemplates, conditionTemplateCheck, conditionTemplate,
  claimStoreDiagnosisTariff, claimStoreProcedureTariff,
  claimStoreInvestigationTariff, claimStoreMedicationCatalogue,
  //  The claim editor's own Service dropdown reads this. One call answers both visit
  //  fields — the services for this age and setting, and the four NHIA attendance codes.
  claimStoreServices,
} from '../BaseURL';

// ============================================================================
//  Condition Templates — "for THIS condition, in THIS patient, give THIS"
// ----------------------------------------------------------------------------
//  Built out of the facility's OWN priced catalogues — the same rows the claim editor
//  bills from — so a code chosen here is a code that editor would accept.
//
//  ── THE SHAPE OF THE SCREEN, AND WHY IT CHANGED ─────────────────────────────
//
//  The first version stacked every step down one column: a diagnosis list, then the
//  sex and age pills, then THREE catalogue lists open at once. Reported as "they are
//  all jumbled together… I don't seem to see how to even set it out", which is exactly
//  right — four scrolling lists visible at the same time are four things competing for
//  the same decision, and none of them is the one you are making.
//
//  So it is three panes:
//
//    ┌──────────────────┬─────────────────────┬────────────────────┐
//    │ THE STEPS        │ ONE STEP AT A TIME  │  THE TEMPLATE      │
//    │ 1 Who it is for ✓│                     │  what you have     │
//    │   Adult · Female │ only the active     │  built so far,     │
//    │ 2 Condition    ✓ │ step's picker is on │  always visible    │
//    │ 3 Procedures     │ screen              │                    │
//    │ 4 Labs         2 │                     │  [ Save ]          │
//    │ 5 Medicines    1 │                     │                    │
//    └──────────────────┴─────────────────────┴────────────────────┘
//
//  THE RAIL CARRIES STATE. Each step shows a tick when it is answered and a count when
//  it holds something, so "what have I still to do" is answered by looking rather than
//  by scrolling. That is the whole reason a rail beats a long form for somebody who is
//  not an IT user: the shape of the task is visible before the task is done.
//
//  THE TEMPLATE PANE IS NEVER HIDDEN. It is the thing being made. A form that shows you
//  the controls but not the result makes you hold the result in your head.
//
//  ── THE ORDER OF THE STEPS ──────────────────────────────────────────────────
//
//  WHO IT IS FOR COMES FIRST — age band, then gender, then out-patient or in-patient.
//  Not a preference: every catalogue the later steps read from is narrowed by the age
//  band, the DIAGNOSIS list included, because MEDI28A and PAED28C are two entries for
//  one illness. Asked after the condition, the form spends its time refusing things it
//  had just offered. (An earlier version did put the condition first, and had exactly
//  that fault.)
//
//  Step 1 is never locked and is ticked from the outset, because all three of its answers
//  have real defaults: 'both' age, 'both' gender, out-patient. It carries a summary line
//  in place of a count, since a count there would always be nothing.
//
//  Steps 3–5 are held until there is a condition, and the rail says why.
//
//  IN-PATIENT IS SHOWN AND NOT YET SELECTABLE. A template for an admission has to spread
//  its drugs across the days of a stay, which is a different thing from one visit's
//  dispensing; out-patient first, deliberately. It appears greyed with the reason
//  attached rather than left out — an absent option reads as one the officer failed to
//  find. The server refuses it too, so the pill is not the only thing holding the line.
//
//  ── WHERE THE REFUSALS COME FROM ────────────────────────────────────────────
//  The server. Every change re-asks /check, which is the same function the save runs
//  first — a form that decided for itself what was allowed would be a second opinion,
//  and the drifted half would be the one letting a wrong template through.
// ============================================================================

const T = {
  ink: '#0F172A',
  inkSoft: '#334155',
  inkMuted: '#64748B',
  inkFaint: '#94A3B8',
  line: '#E2E8F0',
  head: '#F1F5F9',
  canvas: '#F8FAFC',
  green: '#0F5132',
  greenSoft: '#E7F3EC',
  danger: '#B42318',
  dangerSoft: '#FEF3F2',
  caution: '#8A5510',
  cautionSoft: '#FFF8EB',
};

const MONO = {
  fontFamily: 'ui-monospace, "Cascadia Mono", Consolas, monospace',
  fontVariantNumeric: 'tabular-nums',
};

//  ── THE THREE CATALOGUES THE "WHAT TO GIVE" STEPS OFFER ─────────────────────
//  Diagnoses are absent on purpose: a diagnosis is the CONDITION, chosen in step 1, and
//  one in both halves would be written to a claim twice.
//
//  Ordered procedure → lab → medication because that is the order a claim is built in,
//  and so the order an officer reads one in.
const KINDS = [
  { key: 'procedure', label: 'Procedures', one: 'procedure', icon: Scissors,
    url: claimStoreProcedureTariff,
    hint: 'Anything done to the patient for this condition. Search by name or G-DRG.' },
  { key: 'investigation', label: 'Labs & scans', one: 'lab / scan', icon: FlaskConical,
    url: claimStoreInvestigationTariff,
    hint: 'The tests that confirm or follow this condition.' },
  { key: 'medication', label: 'Medications', one: 'medication', icon: PillIcon,
    url: claimStoreMedicationCatalogue,
    hint: 'The drugs, with the dose, frequency and duration to give them at.' },
];

const RX_FIELDS = [
  ['dose_unit', 'Dose unit', '500 mg'],
  ['dose', 'Dose', '500 mg x 1'],
  ['frequency', 'Frequency', ''],
  ['duration', 'Duration', '5'],
  ['route', 'Route', ''],
  ['quantity', 'Quantity', '15'],
];

//  ── THE AGE THE CATALOGUES ARE ASKED FOR ────────────────────────────────────
//  The endpoints narrow on an AGE, not a band, so the band is turned into one age that
//  is unambiguously inside it. 'both' sends nothing — there is no age that is right for
//  both, and narrowing to one would quietly hide the tariffs the other half needs.
//  ── WHAT AGE THE CATALOGUES ARE ASKED FOR ───────────────────────────────────
//  A representative age, not a real one: the endpoints narrow by band and this is how a
//  band is expressed to them. `undefined` means "do not narrow", which is what 'both'
//  wants — and what a custom range that straddles 12 wants, for the same reason.
//
//  A CUSTOM RANGE IS ASKED THROUGH ITS EFFECTIVE BAND, which the server computes and
//  returns, so the pickers narrow by the same answer the refusals are made with. Working
//  it out again here would be a second copy of the ADULT_FROM boundary.
const ageForBand = (band) => (band === 'child' ? 5 : band === 'adult' ? 30 : undefined);

//  How a range reads. The SAME wording the server's ageRangeLabel produces, because the
//  officer sees this while typing and the saved name afterwards — two spellings of one
//  range would read as two ranges.
const ageRangeText = (from, to) => {
  const lo = from == null || from === '' ? null : Number(from);
  const hi = to == null || to === '' ? null : Number(to);
  if (!Number.isFinite(lo) && !Number.isFinite(hi)) return null;
  if (!Number.isFinite(hi)) return `${lo} and over`;
  if (!Number.isFinite(lo)) return `under ${hi + 1}`;
  if (lo === hi) return `${lo} year${lo === 1 ? '' : 's'} old`;
  return `${lo}–${hi}`;
};

//  Identity, matching what each store writer treats as unique — the same rule the
//  single-claim editor and Multi Vet use, so a template cannot hold two of what the
//  writer considers one thing.
//  ── A DIAGNOSIS IS THE (ICD-10, G-DRG) PAIR ─────────────────────────────────
//  Not the ICD-10 alone. B54 "Malaria" is billed on MEDI28A at 133.85 for an adult and
//  on PAED36C at 124.23 for a child — one condition, two genuinely different rows, and
//  118 ICD-10 codes in the live catalogue straddle the two bands like that.
//
//  Keyed on the code alone, ticking either row ticked BOTH, because they produced the
//  same id. claimStore's own picker warns about this at diagnosisTariffOptions ("keying
//  on the code alone would make all five indistinguishable, so picking one would select
//  another") and hands back `${code}::${gdrg}` for exactly this reason; this is the same
//  identity, in the shape the rest of this file uses.
//
//  Reads BOTH shapes — `code`/`gdrg` off a catalogue row, `icd10_code`/`gdrg_code` off a
//  stored condition — so the picker and the template compare as equals. Two spellings of
//  one identity is how a tick comes to disagree with what was saved.
const idOf = (kind, row) => {
  const up = (v) => String(v || '').trim().toUpperCase();
  if (kind === 'procedure') return `procedure:${up(row.gdrg || row.gdrg_code || row.code)}`;
  if (kind === 'diagnosis') {
    //  AND THE NAME. The pair is not unique either: 88 of the 594 offered NHIA rows
    //  share an (ICD-10, G-DRG) pair with another row — "Acute Cystitis" and
    //  "Cystitis" are both N30.00/MEDI31A, at the same tariff; 12 such pairs are
    //  priced differently. Without the name, ticking one ticked both, which is the
    //  reported fault one level down from the one this key already fixed.
    //
    //  `up` trims and upper-cases but leaves punctuation alone, which matters:
    //  A08.2/MEDI23A is "Bacteria Enteritis" (adult, MEDI) and "Bacteria Enteritis."
    //  (child, PAED) at different tariffs, and the full stop is all that separates
    //  them.
    return `diagnosis:${up(row.code || row.icd10_code)}::${up(row.gdrg || row.gdrg_code)}`
         + `::${up(row.name)}`;
  }
  return `${kind}:${up(row.code)}`;
};

//  How a row of each catalogue is read. The diagnosis list shows the ICD-10 AND the
//  G-DRG, because both matter: the ICD-10 is the condition's identity and the G-DRG is
//  what says whether it is an adult or a child tariff.
const LABELS = {
  diagnosis: {
    kind: 'diagnosis',
    of: (r) => ({ title: r.name, sub: [r.code, r.gdrg].filter(Boolean).join(' · '),
      tariff: r.tariff }),
  },
  procedure: {
    kind: 'procedure',
    of: (r) => ({ title: r.name, sub: r.gdrg || r.code, tariff: r.tariff }),
  },
  investigation: {
    kind: 'investigation',
    of: (r) => ({ title: r.name, sub: r.code, tariff: r.tariff }),
  },
  medication: {
    kind: 'medication',
    of: (r) => ({ title: r.name,
      sub: [r.code, r.dose_unit].filter(Boolean).join(' · '), tariff: r.tariff }),
  },
};

// ── How wide the window is ──────────────────────────────────────────────────
//  Three panes need room. Below that the rail becomes a row of tabs and the template
//  pane drops underneath — the same content, stacked, rather than three columns
//  squeezed into a phone. Inline styles cannot carry a media query, so the breakpoint
//  is read once and listened to.
function useWide(px) {
  const [wide, setWide] = useState(() => (typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    ? window.matchMedia(`(min-width: ${px}px)`).matches
    : true));
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(`(min-width: ${px}px)`);
    const on = (e) => setWide(e.matches);
    //  addEventListener where it exists, addListener where it does not — Safari only
    //  grew the modern one in 14, and a screen that silently stops being responsive is
    //  worse than one that never was.
    if (mq.addEventListener) mq.addEventListener('change', on);
    else mq.addListener(on);
    setWide(mq.matches);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', on);
      else mq.removeListener(on);
    };
  }, [px]);
  return wide;
}

// ── HOW WELL A ROW MATCHES WHAT WAS TYPED ───────────────────────────────────
//
//  The endpoint answers "does this row match" with SQL LIKE, and then sorts the matches
//  ALPHABETICALLY (cleanestFirst). For one match that is fine. For "m" it returns 88 rows
//  with Malaria somewhere in the middle of the alphabet — reported as "if malaria, I
//  should see that one first before any other thing".
//
//  So matches are ranked here by how well they match, not by their name. An exact name
//  beats a prefix, a prefix beats a word inside the name, and a code match beats a
//  substring buried mid-word. Ties keep the server's own order, so cleanestFirst still
//  decides between two equally good matches and suspect rows still sort last.
const reQuote = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const rankRow = (row, needle) => {
  if (!needle) return 0;
  const name = String(row.name || '').toLowerCase();
  //  Every code the row can be found by. The diagnosis list is searchable on ICD-10 and
  //  G-DRG as well as name, and the placeholder promises all three.
  const codes = [row.code, row.icd10, row.gdrg, row.gdrg_code]
    .filter(Boolean).map((c) => String(c).toLowerCase());

  if (name === needle) return 100;
  if (codes.some((c) => c === needle)) return 95;
  if (name.startsWith(needle)) return 90;
  if (codes.some((c) => c.startsWith(needle))) return 75;
  //  A whole word inside the name: "Severe Malaria" for "malaria" beats a row that
  //  merely contains the letters somewhere.
  if (new RegExp(`\\b${reQuote(needle)}`).test(name)) return 60;
  if (name.includes(needle)) return 40;
  if (codes.some((c) => c.includes(needle))) return 25;
  return 0;
};

// ── A searchable catalogue list ─────────────────────────────────────────────
//  One component for all four, because they differ only in which endpoint they read and
//  what a row is labelled by. Four copies of a debounced searchable list would drift.
//
//  `only` hides rows the CURRENT TEMPLATE cannot carry — the age filter. `hiddenNote`
//  says how many went and why, because a list that quietly shrinks is a list the officer
//  will assume is the whole catalogue. `blockedBy` returns a REASON a visible row cannot
//  be added, which is different from hiding it: the row is relevant, it just is not
//  available yet.
//
//  ── IT NARROWS ON THE KEYSTROKE, NOT ON THE ANSWER ─────────────────────────
//  Reported as "it is not showing me directly". The query itself takes 4-10 ms, so the
//  wait was never the database — it was a 220 ms debounce during which the PREVIOUS list
//  stayed on screen looking current, because the "Searching…" line only appeared when the
//  list was empty. Typing over a 218-row catalogue therefore showed 218 unrelated rows.
//
//  So the rows already in hand are filtered and ranked locally on every keystroke, and
//  the server's answer reconciles behind it. A narrower query can only ever REMOVE rows
//  from a set already fetched, so the local answer is never wrong — at worst incomplete,
//  which the box says while a request is in flight.
const ROW_CAP = 120;

//
//  `keyOf` decides what a row's tick is keyed on. It defaults to the row's own identity,
//  and an in-patient step passes one that carries the DAY being filled in — so the same
//  drug reads as un-picked on day 2 after being added to day 1, which is the only way a
//  multi-day chart can be built.
function Catalogue({
  url, params, placeholder, onPick, chosenIds, rowLabel, note, tall,
  only, hiddenNote, blockedBy, keyOf,
}) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  //  WHICH QUERY `rows` ACTUALLY ANSWERS. Without it there is no way to tell a list that
  //  is current from one left over from three keystrokes ago, which is the whole bug.
  const [rowsFor, setRowsFor] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  //  Every fetch carries a token. A slow answer for "mal" must not land on top of a
  //  later one for "malaria" — the list would show the wrong catalogue for the box.
  const token = useRef(0);

  const key = JSON.stringify(params);
  useEffect(() => {
    const mine = (token.current += 1);
    //  Captured, not read from the closure later: `rowsFor` must record the string that
    //  was actually sent, or the staleness test compares against the wrong query.
    const sent = q.trim();
    //  140 ms, down from 220. The query is 4-10 ms; the debounce is there to spare the
    //  server a request per keystroke, not to cover latency, and the local narrowing
    //  above means nothing is waiting on it.
    const t = setTimeout(() => {
      setBusy(true);
      setErr(null);
      axios.get(url, { params: { scheme: 'NHIS', ...params, search: sent || undefined } })
        .then(({ data }) => {
          if (mine !== token.current) return;
          setRows(data?.options || []);
          setRowsFor(sent);
          setBusy(false);
        })
        .catch((e) => {
          if (mine !== token.current) return;
          setRows([]);
          setRowsFor(sent);
          setErr(e?.response?.data?.message || e.message);
          setBusy(false);
        });
    }, 140);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, key, q]);

  //  Filtered here rather than by the endpoint: the age rule this applies belongs to a
  //  TEMPLATE, not to a claim. On a real claim the patient's age is known and the server
  //  narrows correctly; a template for "any age" is asking a question the endpoint has no
  //  way to be asked. Filtering here keeps the claim editor's picker untouched.
  //
  //  Ahead of the search, so `hidden` counts what the AGE rule removed and its note stays
  //  true whatever is typed.
  const usable = typeof only === 'function' ? rows.filter(only) : rows;
  const hidden = rows.length - usable.length;

  // ── NARROWED AND RANKED, WITHOUT WAITING FOR THE SERVER ───────────────────
  const needle = q.trim().toLowerCase();
  //  The rows in hand answer a different question from the one now in the box.
  const stale = needle !== rowsFor.trim().toLowerCase();
  const scored = usable.map((r, i) => ({ r, i, s: rankRow(r, needle) }));
  //  DROPPED only while stale. Once the server has answered this exact query every row it
  //  returned is a match by definition — including ones matched on a column this ranking
  //  does not read — so a zero score then means "rank it last", never "hide it".
  const narrowed = (needle && stale) ? scored.filter((x) => x.s > 0) : scored;
  //  Ties keep the server's order: cleanestFirst already decided between equal matches,
  //  and it is the thing that sorts malformed rows last.
  narrowed.sort((a, b) => (b.s - a.s) || (a.i - b.i));
  const matched = narrowed.map((x) => x.r);

  const shown = matched.slice(0, ROW_CAP);
  //  SAID, not silently dropped. "Malaria" sits at row 115 of the 218-row child
  //  catalogue, so the old cap of 80 hid it completely while browsing — the officer
  //  scrolled a list that had already thrown their row away.
  const capped = matched.length - shown.length;
  //  A request is out and what is on screen may not be the whole answer yet. Only worth
  //  saying while something is actually missing or in flight.
  const searching = busy || stale;

  return (
    <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ position: 'relative', marginBottom: 8, flexShrink: 0 }}>
        <Search size={14} style={{
          position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
          color: T.inkFaint, pointerEvents: 'none',
        }} />
        {/*  autoFocus: the step has just been opened by a click on the rail, and the
             next thing anybody does is type. */}
        <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus
          placeholder={placeholder}
          style={{
            width: '100%', boxSizing: 'border-box',
            //  Room on the right for the state chip, so it never sits on the text.
            padding: '10px 84px 10px 31px',
            borderRadius: 9, font: 'inherit', fontSize: 13, background: '#fff',
            border: `1px solid ${T.line}`, color: T.ink,
          }} />
        {/*  ── THE STATE OF THE LIST, IN THE BOX ──────────────────────────────
             The count of what is on screen, or "Searching…" while an answer is
             outstanding. Before this, a list left over from an earlier query looked
             exactly like a current one — which is how typing "Malaria." came to show 218
             unrelated rows with nothing to say they were stale. */}
        <span style={{
          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          fontSize: 10.5, fontWeight: 700, pointerEvents: 'none',
          color: searching ? T.caution : T.inkFaint, ...MONO,
        }}>
          {searching ? 'Searching…' : (needle ? `${matched.length} found` : null)}
        </span>
      </div>
      {note && (
        <div style={{
          fontSize: 11.5, color: T.caution, marginBottom: 8, lineHeight: 1.5,
          flexShrink: 0,
        }}>{note}</div>
      )}
      {err && (
        <div style={{ fontSize: 11.5, color: T.danger, marginBottom: 6, flexShrink: 0 }}>{err}</div>
      )}
      {/*  What `only` took out. A list that silently shrinks reads as the whole
           catalogue, and the officer then reports a missing diagnosis. */}
      {hidden > 0 && typeof hiddenNote === 'function' && (
        <div style={{
          fontSize: 11.5, color: T.inkMuted, marginBottom: 8, lineHeight: 1.55,
          flexShrink: 0,
        }}>{hiddenNote(hidden)}</div>
      )}
      <div style={{
        display: 'grid', gap: 4, overflowY: 'auto', minHeight: 0,
        maxHeight: tall ? 460 : 300, paddingRight: 2,
      }}>
        {shown.length === 0 && searching ? (
          <span style={{ fontSize: 12.5, color: T.inkFaint }}>Searching…</span>
        ) : shown.length === 0 ? (
          <span style={{ fontSize: 12.5, color: T.inkFaint }}>
            {/*  "Nothing matches malaria" would be a LIE when three things matched and
                 `only` took all three — and it is the case the officer meets first, since
                 an any-age template can carry almost no condition. The reason is in the
                 note above; this line only has to stop contradicting it. */}
            {hidden > 0
              ? (rows.length === 1
                ? 'The one match cannot go on this template — see above.'
                : `None of the ${rows.length} matches can go on this template — see above.`)
              : (q.trim() ? `Nothing matches “${q.trim()}”.` : 'Nothing to show.')}
          </span>
        ) : shown.map((row, i) => {
          //  keyOf when the step supplies one — an in-patient step keys the tick on
          //  the day it is filling in. Falls back to the row's bare identity.
          const rowKey = typeof keyOf === 'function'
            ? keyOf(rowLabel.kind, row) : idOf(rowLabel.kind, row);
          const on = chosenIds.has(rowKey);
          //  A row already on the template is not "blocked" — it is done. Checked in that
          //  order so a chosen row keeps its tick rather than being greyed out.
          const blocked = on ? null
            : (typeof blockedBy === 'function' ? blockedBy(row) : null);
          const { title, sub, tariff } = rowLabel.of(row);
          //  THE POSITION IS PART OF THE KEY. The catalogue holds rows that are identical
          //  once displayed — two "Marasmus / E41 / PAED05C" differing only in a column
          //  this list does not show — and React needs those to be two keys. The identity
          //  still leads, so the ordinary case reconciles on it.
          return (
            <button key={`${rowKey}#${i}`}
              type="button" disabled={on || !!blocked}
              title={on ? 'Already on this template' : (blocked || 'Add it')}
              onClick={() => !blocked && onPick(row)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 9, minWidth: 0,
                textAlign: 'left', font: 'inherit', padding: '8px 10px', borderRadius: 8,
                cursor: (on || blocked) ? 'default' : 'pointer',
                border: `1px solid ${on ? `${T.green}44` : T.line}`,
                background: on ? T.greenSoft : (blocked ? T.head : '#fff'),
              }}>
              {/*  A plus that becomes a tick. The row is a button that adds something,
                   and once added it says so where the plus was rather than only by
                   changing colour — a tint alone is a state you have to compare
                   against the other rows to read. A blocked row shows neither: there is
                   nothing to add and nothing was added. */}
              <span style={{
                flexShrink: 0, width: 18, height: 18, borderRadius: 5, display: 'grid',
                placeItems: 'center', marginTop: 1,
                border: `1px solid ${on ? T.green : T.line}`,
                background: on ? T.green : '#fff', color: on ? '#fff' : T.inkFaint,
              }}>
                {on ? <Check size={11} /> : blocked ? <Ban size={10} /> : <Plus size={11} />}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{
                  display: 'block', fontSize: 12.5, fontWeight: 650,
                  color: blocked ? T.inkMuted : T.ink,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{title}</span>
                <span style={{
                  display: 'block', fontSize: 10.5, color: T.inkFaint, ...MONO,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{sub}</span>
                {/*  The reason, ON the row and not only in a tooltip. A greyed row whose
                     explanation needs a hover is a dead end on a touch screen, and this
                     screen is not for somebody who will go hunting. */}
                {blocked && (
                  <span style={{
                    display: 'block', fontSize: 10.5, color: T.caution, marginTop: 3,
                    lineHeight: 1.45, whiteSpace: 'normal',
                  }}>{blocked}</span>
                )}
              </span>
              {tariff != null && (
                <span style={{
                  flexShrink: 0, fontSize: 11.5, color: T.inkMuted, ...MONO, marginTop: 1,
                }}>{Number(tariff).toFixed(2)}</span>
              )}
            </button>
          );
        })}
      </div>
      {/*  ── THE CAP, SAID ────────────────────────────────────────────────────
           "Malaria" is row 115 of the 218-row child catalogue, so a cap that silently
           renders the first N threw it away and left the officer scrolling for it. The
           cap stays — a thousand buttons is not a list — but it now says so, and says
           what to do about it. */}
      {capped > 0 && (
        <div style={{
          fontSize: 11, color: T.inkMuted, marginTop: 7, flexShrink: 0, lineHeight: 1.5,
        }}>
          Showing the closest {ROW_CAP} of {matched.length}
          {needle ? '' : ' — type a name, ICD-10 or G-DRG to narrow it'}.
        </div>
      )}
    </div>
  );
}

//  `off` is a REASON a pill cannot be chosen, not a boolean — shown on hover, and printed
//  under the group too, so a greyed-out option never looks like something the officer
//  failed to find.
function Pill({ on, off, onClick, children, title }) {
  return (
    <button type="button" aria-pressed={on} disabled={!!off}
      onClick={off ? undefined : onClick} title={off || title}
      style={{
        padding: '8px 15px', borderRadius: 999, font: 'inherit', fontSize: 12.5,
        fontWeight: on ? 800 : 650, cursor: off ? 'not-allowed' : 'pointer',
        opacity: off ? 0.45 : 1,
        border: `1px solid ${on ? T.green : T.line}`,
        background: on ? T.green : '#fff',
        color: on ? '#fff' : T.inkSoft,
      }}>{children}</button>
  );
}

//  A line in the template pane. Used for conditions and for all three item kinds, so
//  they read as one list of things rather than four differently-shaped ones.
//
//  `star` turns the row into the one thing an admission is billed on. Present only when
//  a choice is actually open — see THE PRINCIPAL below — so an out-patient template, and
//  an admission's diagnoses once a procedure exists, show no star at all rather than a
//  dead one.
function Line({ code, name, detail, tone, onRemove, removeLabel, star }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 0',
      borderTop: `1px solid ${T.line}`, minWidth: 0,
    }}>
      {star && (
        <button type="button" onClick={star.onToggle}
          aria-pressed={!!star.on}
          //  The reason, on the control. "Principal" alone means nothing to somebody who
          //  has not read the tariff rules.
          title={star.on
            ? 'This is what the admission is billed on'
            : 'Make this the one the admission is billed on'}
          style={{
            flexShrink: 0, marginTop: 2, border: 0, background: 'none', padding: 0,
            cursor: 'pointer', lineHeight: 1,
            color: star.on ? T.green : T.inkFaint,
          }}>
          <Star size={14} fill={star.on ? T.green : 'none'} />
        </button>
      )}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          display: 'block', fontSize: 12.5, fontWeight: 650,
          color: tone || T.ink, lineHeight: 1.35,
        }}>{name}</span>
        <span style={{
          display: 'block', fontSize: 10.5, color: T.inkFaint, ...MONO,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{code}</span>
        {detail && (
          <span style={{
            display: 'block', fontSize: 11, color: T.inkMuted, lineHeight: 1.45,
            marginTop: 1,
          }}>{detail}</span>
        )}
      </span>
      {onRemove && (
        <button type="button" onClick={onRemove} aria-label={removeLabel}
          title={removeLabel}
          style={{
            flexShrink: 0, border: 0, background: 'none', cursor: 'pointer',
            color: T.inkFaint, padding: 2, lineHeight: 1, marginTop: 2,
          }}><X size={13} /></button>
      )}
    </div>
  );
}

//  ageBand and sex default to 'both' — a real answer, and the commonest. `setting` has no
//  'both' and defaults to the one that exists.
const BLANK = {
  id: null, conditions: [], ageBand: 'both', ageFrom: null, ageTo: null,
  sex: 'both', setting: 'outpatient', stayDays: null,
  //  The two visit fields. null on both means the template sets neither and leaves the
  //  claim's own alone — which is what every template written before them did.
  attendance: null, service: null,
  items: [], name: '', note: '',
};

export default function ConditionTemplates() {
  const [list, setList] = useState(null);
  const [vocab, setVocab] = useState({ sexLabels: {}, ageLabels: {} });
  const [rxVocab, setRxVocab] = useState({ frequencies: [], routes: [], byCode: new Map() });
  const [loadErr, setLoadErr] = useState(null);

  const [draft, setDraft] = useState(BLANK);
  const [building, setBuilding] = useState(false);
  //  'who' is step 1 — see THE ORDER OF THE STEPS at the top of this file.
  const [step, setStep] = useState('who');
  const [check, setCheck] = useState(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(() => new Set());
  const [q, setQ] = useState('');
  //  WHICH SETTING THE LIBRARY IS SHOWING: 'all', 'outpatient' or 'inpatient'.
  //  An out-patient template and an in-patient one for the same condition are
  //  different documents — different principal rule, different days, and Multi Vet
  //  refuses each on the other's claims — so "show me the admissions" is a question
  //  the library has to be able to answer without reading every row's badge.
  const [setting, setSetting] = useState('all');

  //  1180 for three panes, 780 for two. Below that everything stacks.
  const wide = useWide(1180);
  const medium = useWide(780);

  // ── The library ───────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoadErr(null);
    try {
      const { data } = await axios.get(conditionTemplates);
      setList(data?.templates || []);
      setVocab({
        sexes: data?.sexes || ['both', 'male', 'female'],
        ageBands: data?.ageBands || ['both', 'child', 'adult', 'custom'],
        adultFrom: data?.adultFrom || 12,
        ageMax: data?.ageMax || 120,
        sexLabels: data?.sexLabels || {},
        ageLabels: data?.ageLabels || {},
        //  Both lists. `settings` is what to SHOW, `settingsAvailable` is what may be
        //  chosen — the difference is how in-patient appears as deferred rather than
        //  missing. The fallbacks match the server's own, for the case where an older
        //  build answers this call.
        settings: data?.settings || ['outpatient', 'inpatient'],
        settingsAvailable: data?.settingsAvailable || ['outpatient'],
        settingLabels: data?.settingLabels || {},
        settingNotes: data?.settingNotes || {},
        //  The four NHIA attendance codes and which of them are women's care. Read from
        //  the server so the pill this screen greys out and the refusal the endpoint
        //  makes come from one list.
        attendances: data?.attendances || ['EAE', 'CFU', 'ANC', 'PNC'],
        attendanceLabels: data?.attendanceLabels || {},
        femaleOnlyAttendances: data?.femaleOnlyAttendances || ['ANC', 'PNC'],
      });
    } catch (e) {
      setList([]);
      setLoadErr(e?.response?.data?.error || e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  //  The prescription vocabulary — the controlled frequency and route lists the store
  //  validates against, so a value chosen here cannot be one the write refuses.
  //
  //  It comes off the MEDICATION CATALOGUE endpoint rather than a vocabulary endpoint of
  //  its own: that response already carries `frequencies` and `routes` alongside the
  //  drugs, and `byCode` — the dose unit a picked drug arrives with — has to come from
  //  the same read as the drugs or a drug gets prefilled from a different book.
  useEffect(() => {
    let alive = true;
    axios.get(claimStoreMedicationCatalogue, { params: { scheme: 'NHIS' } })
      .then(({ data }) => {
        if (!alive) return;
        const byCode = new Map();
        for (const o of data?.options || []) byCode.set(String(o.code || '').trim(), o);
        setRxVocab({ frequencies: data?.frequencies || [], routes: data?.routes || [], byCode });
      })
      .catch(() => { if (alive) setRxVocab({ frequencies: [], routes: [], byCode: new Map() }); });
    return () => { alive = false; };
  }, []);

  // ── WHICH BAND THE PICKERS NARROW BY ──────────────────────────────────────
  //  'custom' is not a band the tariff book knows, so it must be translated before any
  //  catalogue is asked — and it cannot wait for /check, because the diagnosis list is
  //  narrowed before a condition exists. That is the whole reason step 1 comes first.
  //
  //  The BOUNDARY comes from the server (vocab.adultFrom), so the 12 is defined once. The
  //  mapping is the same one claimRules.bandForRange makes: entirely below reads child,
  //  entirely on-or-above reads adult, straddling reads 'both' — which narrows nothing,
  //  correctly, because a range holding both kinds of patient can carry no banded tariff.
  //
  //  An open end is treated as the extreme it means: no lower bound is 0, no upper bound
  //  is the oldest age there is.
  const effBand = useMemo(() => {
    if (draft.ageBand !== 'custom') return draft.ageBand;
    const line = Number(vocab.adultFrom) || 12;
    const lo = draft.ageFrom == null ? 0 : Number(draft.ageFrom);
    const hi = draft.ageTo == null ? (Number(vocab.ageMax) || 120) : Number(draft.ageTo);
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi < lo) return 'both';
    if (hi < line) return 'child';
    if (lo >= line) return 'adult';
    return 'both';
  }, [draft.ageBand, draft.ageFrom, draft.ageTo, vocab.adultFrom, vocab.ageMax]);

  // ── THE SERVICES THIS TEMPLATE MAY SET ────────────────────────────────────
  //  The same list the claim editor's Service dropdown offers, narrowed the same way:
  //  by the age band and by out-patient versus in-patient. `nights: 0` because an
  //  out-patient template is one day by definition — without it the endpoint offers the
  //  admission catalogue's fallbacks too.
  //
  //  RE-READ WHEN THE AGE BAND MOVES, because the list is banded: an adult template must
  //  not be offered a paediatric consultation code. That is the same narrowing the
  //  diagnosis and drug catalogues get, and the reason step 1 comes first.
  const [services, setServices] = useState({ options: [], note: null, loading: true });
  useEffect(() => {
    let alive = true;
    setServices((s) => ({ ...s, loading: true }));
    axios.get(claimStoreServices, {
      params: {
        age: ageForBand(effBand),
        attendance: draft.setting === 'inpatient' ? 'inpatient' : 'outpatient',
        nights: draft.setting === 'inpatient' ? undefined : 0,
      },
    })
      .then(({ data }) => {
        if (!alive) return;
        setServices({
          options: data?.options || [], note: data?.note || null, loading: false,
        });
      })
      .catch((e) => {
        if (!alive) return;
        setServices({
          options: [], loading: false,
          note: e?.response?.data?.message || e.message,
        });
      });
    return () => { alive = false; };
  }, [effBand, draft.setting]);

  //  A service already chosen that the narrowed list no longer holds. Changing the age
  //  band after picking one would otherwise leave a paediatric code on an adult
  //  template, saved and invisible — the server would refuse it, but only on save.
  const serviceStale = !!(draft.service && !services.loading
    && services.options.length
    && !services.options.some((o) => String(o.code).toUpperCase()
      === String(draft.service.code).toUpperCase()));

  // ── The draft, checked by the server on every change ──────────────────────
  const draftKey = JSON.stringify({
    c: draft.conditions.map((x) => x.icd10_code),
    s: draft.sex, a: draft.ageBand, st: draft.setting,
    //  The range changes the suggested name, the effective band and therefore which
    //  tariffs are refused, so it has to re-ask the server like any other axis.
    af: draft.ageFrom, atv: draft.ageTo,
    //  Both visit fields are in the key: each changes the suggested name and the
    //  attendance can be refused outright on a male template.
    at: draft.attendance || '', sv: draft.service ? draft.service.code : '',
    //  The stay length changes the name, the guards and which days are legal.
    sd: draft.stayDays,
    //  And the DAY and the STAR on every row — the day places a dose, the star decides
    //  what the claim is billed on, and both are refused server-side when wrong.
    dy: draft.items.map((x) => `${x.day ?? ''}:${x.is_principal ? 1 : 0}`).join(','),
    pc: draft.conditions.map((x) => (x.is_principal ? 1 : 0)).join(''),
    i: draft.items.map((x) => `${x.kind}:${x.code}:${x.dose_unit || ''}:${x.dose || ''}`
      + `:${x.frequency || ''}:${x.duration || ''}:${x.route || ''}:${x.quantity || ''}`),
    n: draft.name,
  });
  useEffect(() => {
    if (!building || !draft.conditions.length) { setCheck(null); return undefined; }
    const t = setTimeout(() => {
      axios.post(conditionTemplateCheck, draft)
        .then(({ data }) => setCheck(data))
        .catch((e) => setCheck({
          ok: false, canSave: false, conflicts: [],
          warnings: [e?.response?.data?.error || e.message],
        }));
    }, 260);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, building]);

  // ── WHICH DAY IS BEING FILLED IN ──────────────────────────────────────────
  //  An admission's chart is built a day at a time: pick day 2, then pick the drugs for
  //  day 2. That reads the way the officer described it — "first day you produce the
  //  drugs, second day the drugs, third day then drugs" — and it is the only way the
  //  SAME drug can be added three times without the picker calling the second one a
  //  duplicate.
  const [activeDay, setActiveDay] = useState(1);
  //  Clamped, not left dangling. Shortening the stay from 5 to 3 while sitting on day 5
  //  would leave the picker adding to a day the template no longer has.
  useEffect(() => {
    if (draft.setting !== 'inpatient') { setActiveDay(1); return; }
    const max = Number(draft.stayDays) || 1;
    setActiveDay((d) => Math.min(Math.max(1, d), max));
  }, [draft.setting, draft.stayDays]);

  //  Built through idOf, not by hand. The hand-written version keyed diagnoses on the
  //  ICD-10 alone while the picker keyed them on the pair, so one click ticked two rows.
  //
  //  ── ON AN ADMISSION THE DAY IS PART OF THE TICK ──────────────────────────
  //  Paracetamol on day 1 must leave Paracetamol pickable for day 2, or a three-day
  //  course cannot be built at all. So the key carries the day, and the picker's tick
  //  answers "is this on the day I am filling in" rather than "is this anywhere on the
  //  template". A DIAGNOSIS has no day — it belongs to the visit, not to a day of it —
  //  and keeps the bare pair, which is also what the server's own dedupe does.
  const byDay = draft.setting === 'inpatient';
  const itemKey = useCallback((kind, row, day) => (
    byDay && kind !== 'diagnosis'
      ? `${idOf(kind, row)}@${day == null ? '' : day}`
      : idOf(kind, row)
  ), [byDay]);

  const chosenIds = useMemo(() => {
    const s = new Set(draft.conditions.map((c) => idOf('diagnosis', c)));
    for (const it of draft.items) s.add(itemKey(it.kind, it, it.day));
    return s;
  }, [draft, itemKey]);

  //  ── WHICH CONDITIONS THE AGE BAND ALLOWS ──────────────────────────────────
  //  `age_scope` comes off every catalogue row: 'adult', 'child', or 'all'. It is
  //  claimRules.ageBandOf's answer, read from the G-DRG group and the A/C suffix rather
  //  than from the importer's guess at the name — so MEDI is adult and PAED is child.
  //
  //  For Child or Adult the ENDPOINT has already filtered (it is passed the age), and
  //  this agrees with it rather than second-guessing it. The case it exists for is ANY
  //  AGE, where the endpoint is given no age and correctly returns everything: an
  //  age-priced tariff on a template for both bands is wrong for half the patients it
  //  would be applied to, and the server refuses it on save. Hiding it here means the
  //  officer never picks a thing that is going to be refused.


  const conditionFitsBand = useCallback((row) => {
    const scope = String(row.age_scope || 'all');
    if (effBand === 'both') return scope === 'all';
    return scope === 'all' || scope === effBand;
  }, [effBand]);

  //  ── AND THE SIBLING ROWS ──────────────────────────────────────────────────
  //  A claim is billed on ONE G-DRG per diagnosis, so a template holds B54 once. With the
  //  tick now keyed on the pair, the OTHER B54 row shows an inviting plus that would be
  //  silently dropped as a duplicate on save. So it is closed with a reason instead —
  //  naming the tariff already chosen, which is the thing the officer needs in order to
  //  decide whether to swap.
  const conditionBlock = useCallback((row) => {
    const icd = String(row.code || row.icd10_code || '').trim().toUpperCase();
    const gdrg = String(row.gdrg || row.gdrg_code || '').trim().toUpperCase();
    const held = draft.conditions.find((c) => String(c.icd10_code).toUpperCase() === icd);
    if (!held || String(held.gdrg_code || '').toUpperCase() === gdrg) return null;
    return `${icd} is already on this template, billed on ${held.gdrg_code}. `
         + 'A claim carries one tariff per diagnosis — remove that one first to use this.';
  }, [draft.conditions]);

  //  The name shown: what the officer typed, or the server's suggestion. Never a third
  //  thing computed here — that is how a form comes to disagree with what it saves.
  const shownName = draft.name || check?.template?.suggestedName || '';

  const startNew = () => { setDraft(BLANK); setCheck(null); setStep('who'); setBuilding(true); };
  const editOne = (t) => {
    setDraft({
      id: t.id, conditions: t.conditions, ageBand: t.ageBand, sex: t.sex,
      ageFrom: t.ageFrom ?? null, ageTo: t.ageTo ?? null,
      setting: t.setting || 'outpatient',
      stayDays: t.stayDays ?? null,
      //  Null on a template written before these fields existed, which is correct: it
      //  set neither and must keep setting neither until somebody chooses.
      attendance: t.attendance || null,
      service: t.service || null,
      items: t.items, name: t.nameIsCustom ? t.name : '', note: t.note || '',
    });
    setCheck(null);
    //  An EDIT opens on the condition, not on step 1. Who it is for is already decided and
    //  rarely the thing being changed; the regimen is.
    setStep('condition');
    setBuilding(true);
  };
  const closeBuilder = () => { setBuilding(false); setDraft(BLANK); setCheck(null); };

  const addCondition = (row) => setDraft((d) => ({
    ...d,
    conditions: [...d.conditions, {
      icd10_code: String(row.code || '').toUpperCase(),
      gdrg_code: row.gdrg ? String(row.gdrg).toUpperCase() : null,
      name: row.name || row.code,
      tariff: row.tariff ?? null,
    }],
  }));

  const addItem = (kind, row) => setDraft((d) => {
    const base = {
      kind,
      code: String((kind === 'procedure' ? (row.gdrg || row.code) : row.code) || '')
        .toUpperCase(),
      name: row.name || row.code,
      tariff: row.tariff ?? null,
      //  ── WHICH DAY OF THE STAY IT GOES ON ──────────────────────────────────
      //  The day the officer is currently filling in, so a chart is built by picking a
      //  day and then picking drugs rather than by setting a day on every row after the
      //  fact. Null on an out-patient template — one visit, one day, and a number there
      //  would be noise.
      day: d.setting === 'inpatient' ? activeDay : null,
      //  Never on the way in. The principal is a deliberate choice among what has been
      //  added, made in the list rather than at the moment of picking.
      is_principal: false,
    };
    if (kind !== 'medication') return { ...d, items: [...d.items, base] };
    //  Resolved from the catalogue on pick, exactly as the single-claim editor does it:
    //  the dose unit arrives filled in and the dose starts at one unit, for the officer
    //  to change.
    const opt = rxVocab.byCode.get(String(row.code || '').trim()) || {};
    const du = row.dose_unit || opt.dose_unit || '';
    return {
      ...d,
      items: [...d.items, {
        ...base, dose_unit: du, dose: du ? `${du} x 1` : '',
        frequency: '', duration: '', route: '', quantity: '',
        countable: opt.countable != null ? !!opt.countable : null,
        quantityTyped: false,
      }],
    };
  });

  //  ── ONE ROW, NOT EVERY DAY OF IT ──────────────────────────────────────────
  //  `day` is part of the address. Keyed on the code alone, removing Paracetamol from
  //  day 2 of a three-day chart would take all three — and the officer would see two
  //  rows they did not ask to lose. `day` is undefined on an out-patient template and
  //  the comparison holds, because those rows carry null.
  const dropItem = (kind, code, day = null) => setDraft((d) => ({
    ...d,
    items: d.items.filter((x) => !(x.kind === kind && x.code === code
      && (x.day ?? null) === (day ?? null))),
  }));

  //  ── QUANTITY IS CALCULATED ────────────────────────────────────────────────
  //  units-per-dose × doses-per-day × days, rounded up, and only for a countable form —
  //  a 7-day syrup course is one bottle, not 21 doses. The same arithmetic the
  //  single-claim editor does. A typed number wins and is never overwritten.
  const perDayOf = (freq) => rxVocab.frequencies.find((f) => f.value === freq)?.perDay || null;
  const suggestQty = (v) => {
    if (v.countable === false) return null;
    const per = perDayOf(v.frequency);
    const days = parseInt(String(v.duration || '').replace(/[^\d]/g, ''), 10);
    if (!per || !Number.isFinite(days) || days <= 0) return null;
    const m = String(v.dose || '').match(/x\s*([\d.]+)\s*$/i);
    const units = m ? Number(m[1]) : 1;
    const n = Math.ceil(units * per * days);
    return n > 0 ? String(n) : null;
  };

  //  Addressed by day too, for the same reason as dropItem: editing day 1's dose must
  //  not silently rewrite day 2 and day 3. A three-day course often steps DOWN — 1 g on
  //  the first day, 500 mg after — and one shared edit would make that impossible to
  //  express.
  const patchItem = (kind, code, field, value, day = null) => setDraft((d) => ({
    ...d,
    items: d.items.map((it) => {
      if (!(it.kind === kind && it.code === code
        && (it.day ?? null) === (day ?? null))) return it;
      const next = { ...it, [field]: value };
      if (field === 'quantity') return { ...next, quantityTyped: true };
      if (['dose', 'frequency', 'duration'].includes(field) && !it.quantityTyped) {
        const s = suggestQty(next);
        if (s !== null) return { ...next, quantity: s, quantityAuto: true };
      }
      return next;
    }),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(conditionTemplates, {
        ...draft,
        //  The typed name only. An accepted suggestion is left blank so the template
        //  keeps following its own conditions when one is added later — a name that
        //  froze the moment it was first shown would go stale silently.
        name: draft.name || undefined,
      });
      await load();
      closeBuilder();
      Swal.fire({
        icon: 'success',
        title: data.updated ? 'Template updated' : 'Template saved',
        html: `<div style="text-align:left;font-size:13px;line-height:1.7">`
          + `<b>${String(data.name).replace(/</g, '&lt;')}</b>`
          + ((data.warnings || []).length
            ? `<div style="margin-top:8px;color:#8A5510">${data.warnings
              .map((w) => String(w).replace(/</g, '&lt;')).join('<br>')}</div>`
            : '')
          + '</div>',
      });
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Could not save this template',
        html: `<div style="text-align:left;font-size:13px;line-height:1.7">`
          + `${String(e?.response?.data?.error || e.message).replace(/</g, '&lt;')}`
          + ((e?.response?.data?.conflicts || []).length
            ? `<div style="margin-top:8px">${e.response.data.conflicts
              .map((c) => String(c.why).replace(/</g, '&lt;')).join('<br><br>')}</div>`
            : '')
          + '</div>',
      });
    } finally { setSaving(false); }
  };

  const remove = async (t) => {
    const ok = await Swal.fire({
      icon: 'warning',
      title: 'Delete this template?',
      html: `<div style="text-align:left;font-size:13px;line-height:1.7">`
        + `<b>${String(t.name).replace(/</g, '&lt;')}</b>`
        + `<div style="margin-top:8px">${t.items.length} item(s) for `
        + `${t.conditions.length} condition(s).</div>`
        //  What it does NOT do. Somebody about to delete a template that has been
        //  applied deserves to know the claims stand.
        + `<div style="margin-top:8px">Claims already built from it are not affected. `
        + `Only the template goes.</div></div>`,
      showCancelButton: true,
      confirmButtonText: 'Delete it',
      cancelButtonText: 'Keep',
      focusCancel: true,
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.delete(conditionTemplate(t.id));
      await load();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Could not delete it',
        text: e?.response?.data?.error || e.message });
    }
  };

  //  A TEMPLATE WITH NO SETTING COUNTS AS OUT-PATIENT — the same reading the row
  //  badge gives it, and the same one the backend gives it. Any other answer here
  //  would make a template disappear from both tabs and appear only under "All",
  //  which reads as the screen having lost it.
  const settingOf = useCallback(
    (t) => (String(t.setting || '').toLowerCase() === 'inpatient'
      ? 'inpatient' : 'outpatient'),
    [],
  );

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (list || []).filter((t) => {
      if (setting !== 'all' && settingOf(t) !== setting) return false;
      if (!needle) return true;
      return [
        t.name,
        ...t.conditions.map((c) => `${c.icd10_code} ${c.name}`),
        ...t.items.map((i) => `${i.code} ${i.name}`),
      ].join(' ').toLowerCase().includes(needle);
    });
  }, [list, q, setting, settingOf]);

  //  COUNTED OVER THE SEARCH, not over the whole library. Typing "malaria" and
  //  seeing "In-patient 4" that turns into an empty list on click is the tab lying
  //  about what is behind it. The numbers move with the search box, so a 0 is a
  //  statement — "no in-patient malaria template" — and not a dead tab.
  const settingCounts = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const hit = (list || []).filter((t) => !needle || [
      t.name,
      ...t.conditions.map((c) => `${c.icd10_code} ${c.name}`),
      ...t.items.map((i) => `${i.code} ${i.name}`),
    ].join(' ').toLowerCase().includes(needle));
    return {
      all: hit.length,
      outpatient: hit.filter((t) => settingOf(t) === 'outpatient').length,
      inpatient: hit.filter((t) => settingOf(t) === 'inpatient').length,
    };
  }, [list, q, settingOf]);

  const conflicts = check?.conflicts || [];
  const canSave = !!check?.canSave && draft.conditions.length > 0 && !saving;
  const hasCondition = draft.conditions.length > 0;
  const countOf = (kind) => draft.items.filter((i) => i.kind === kind).length;

  //  ── THE RAIL ──────────────────────────────────────────────────────────────
  //  Derived, so a step added later carries its own count and lock without anything
  //  else being edited. `locked` is a REASON, not a boolean: a disabled control with no
  //  explanation is a dead end, and this screen is for somebody who will not guess.
  //
  //  WHO IS FIRST. Every catalogue the later steps read is narrowed by the age band, the
  //  diagnosis list included — MEDI28A and PAED28C are two entries for one illness. Asked
  //  afterwards, the form spends its time refusing what it had just offered.
  //
  //  Step 1 carries a SUMMARY rather than a count: all three of its answers have real
  //  defaults, so a count would always be nothing and a tick would always be on. The three
  //  values read back instead, which is the one thing about that step worth seeing from
  //  the rail.
  //  The attendance and the service join it only when SET, because they are optional and
  //  a rail line reading "· no service" says nothing an empty space does not.
  const whoSummary = [
    draft.ageBand === 'custom'
      ? (ageRangeText(draft.ageFrom, draft.ageTo) || 'An age range')
      : draft.ageBand === 'both' ? 'Any age'
        : (draft.ageBand === 'child' ? 'Child' : 'Adult'),
    draft.sex === 'both' ? 'either sex' : (vocab.sexLabels?.[draft.sex] || draft.sex),
    vocab.settingLabels?.[draft.setting] || draft.setting,
    draft.stayDays ? `${draft.stayDays} day${draft.stayDays === 1 ? '' : 's'}` : null,
    draft.attendance || null,
    draft.service ? draft.service.code : null,
  ].filter(Boolean).join(' · ');

  const STEPS = [
    { key: 'who', n: 1, label: 'Who it is for', icon: Users,
      sub: whoSummary,
      count: null,
      //  Ticked from the start, honestly: 'both', 'both' and out-patient are answers, not
      //  blanks. Nothing here can be left undone, so nothing here should nag.
      done: true,
      locked: null },
    { key: 'condition', n: 2, label: 'The condition', icon: Stethoscope,
      count: draft.conditions.length,
      done: hasCondition,
      locked: null },
    ...KINDS.map((k, i) => ({
      key: k.key, n: 3 + i, label: k.label, icon: k.icon,
      count: countOf(k.key),
      done: countOf(k.key) > 0,
      locked: hasCondition ? null : 'Pick a condition first',
    })),
  ];

  const activeStep = STEPS.find((s) => s.key === step) || STEPS[0];
  //  A locked step can never be the active one — clicking it is refused, and a state that
  //  becomes locked while open falls back to the step that UNLOCKS it. Only the catalogue
  //  steps lock, and only for want of a condition, so that step is the fallback.
  const effectiveStep = activeStep.locked ? 'condition' : activeStep.key;

  // ── The active pane ───────────────────────────────────────────────────────
  const pane = (() => {
    if (effectiveStep === 'condition') {
      return (
        <>
          <div style={{ fontSize: 12.5, color: T.inkMuted, lineHeight: 1.6, marginBottom: 10 }}>
            What is this template for? One condition, or several that are treated
            together — malaria and typhoid, say.
          </div>

          {/*  ── ANY AGE CANNOT CARRY AN AGE-PRICED CONDITION ──────────────────
               588 of the 594 priced diagnoses in the live catalogue have a separate
               child and adult tariff, so "any age" leaves almost nothing to choose —
               and every one of the 118 straddling codes would otherwise appear twice,
               once per band, with nothing but the G-DRG to tell them apart. That was
               the two Malarias.

               So the band is asked for HERE, with the pills inline: this is where the
               officer is, and sending them back to step 1 for it would be a trip. */}
          {effBand === 'both' && (
            <div style={{
              border: `1px solid ${T.green}33`, background: T.greenSoft,
              borderRadius: 10, padding: '11px 13px', marginBottom: 11,
            }}>
              <div style={{ fontSize: 12.5, color: T.ink, fontWeight: 700, marginBottom: 4 }}>
                Nearly every condition is priced by age
              </div>
              <div style={{ fontSize: 11.5, color: T.inkSoft, lineHeight: 1.6 }}>
                Malaria is billed on <b>PAED36C</b> for a child and <b>MEDI28A</b> for an
                adult, at different tariffs — one condition, two rows. Say which patient
                this template is for and the list will show the right one.
              </div>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 9 }}>
                {(vocab.ageBands || ['both', 'child', 'adult'])
                  .filter((a) => a !== 'both')
                  .map((a) => (
                    <Pill key={a} on={false}
                      onClick={() => setDraft((d) => ({ ...d, ageBand: a }))}>
                      {vocab.ageLabels?.[a] || a}
                    </Pill>
                  ))}
              </div>
            </div>
          )}

          <Catalogue
            url={claimStoreDiagnosisTariff}
            params={{ age: ageForBand(effBand) }}
            placeholder="Search a diagnosis by name, ICD-10 or G-DRG…"
            chosenIds={chosenIds}
            rowLabel={LABELS.diagnosis}
            onPick={addCondition}
            only={conditionFitsBand}
            hiddenNote={(n) => `${n} more ${n === 1 ? 'condition is' : 'conditions are'} `
              + 'priced for a specific age band and cannot go on an "any age" template — '
              + 'they appear as soon as you pick Child or Adult above.'}
            blockedBy={conditionBlock}
            tall />
        </>
      );
    }
    if (effectiveStep === 'who') {
      //  Age, then sex, then status — in that order, because that is the order they
      //  narrow by. Age decides which half of the tariff book the next steps read; sex
      //  rules out one list within it; the status says which kind of claim this becomes.
      const settingsAvailable = vocab.settingsAvailable || ['outpatient'];
      const deferred = (vocab.settings || []).filter((s) => !settingsAvailable.includes(s));
      return (
        <div style={{ display: 'grid', gap: 20 }}>
          <div style={{ fontSize: 12.5, color: T.inkMuted, lineHeight: 1.6 }}>
            Answered first, because these decide which tariffs every later step may
            offer — the diagnosis list included.
          </div>

          <div>
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '.06em',
              textTransform: 'uppercase', color: T.inkFaint, marginBottom: 7,
            }}>Age</div>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {(vocab.ageBands || ['both', 'child', 'adult', 'custom']).map((a) => (
                <Pill key={a} on={draft.ageBand === a}
                  onClick={() => setDraft((d) => ({
                    ...d,
                    ageBand: a,
                    //  The range is CLEARED when a band is chosen, and seeded when
                    //  'custom' is. Left behind, a range from an earlier choice would
                    //  travel on a Child template — where the server ignores it, so the
                    //  form would be showing a narrowing that is not being applied.
                    ...(a === 'custom'
                      ? { ageFrom: d.ageFrom ?? 0, ageTo: d.ageTo ?? 11 }
                      : { ageFrom: null, ageTo: null }),
                  }))}>
                  {vocab.ageLabels?.[a] || a.charAt(0).toUpperCase() + a.slice(1)}
                </Pill>
              ))}
            </div>

            {/*  ── THE RANGE, WHEN IT IS ASKED FOR ────────────────────────────
                 Asked for as "we want to be able to customize it — so maybe we can pick
                 zero to thirty, one to twenty five". The three bands stay; this is a
                 fourth choice beside them, not a replacement.

                 Either end may be blank for an open range, and the hint says which way
                 an open end reads rather than leaving the officer to find out on save. */}
            {draft.ageBand === 'custom' && (
              <div style={{
                display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap',
                marginTop: 10, padding: '11px 13px', borderRadius: 9,
                background: T.greenSoft, border: `1px solid ${T.green}33`,
              }}>
                {[['ageFrom', 'From'], ['ageTo', 'To']].map(([field, label]) => (
                  <label key={field} style={{ display: 'grid', gap: 4 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 800, letterSpacing: '.06em',
                      textTransform: 'uppercase', color: T.inkFaint,
                    }}>{label}</span>
                    <input
                      type="number" min={0} max={120} step={1}
                      value={draft[field] ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDraft((d) => ({
                          ...d,
                          //  '' becomes null, which is the OPEN end — not 0. Coercing a
                          //  cleared box to 0 would silently turn "30 and over" into
                          //  "0 to 30", the opposite range.
                          [field]: v === '' ? null : Number(v),
                        }));
                      }}
                      style={{
                        width: 84, boxSizing: 'border-box', padding: '8px 10px',
                        borderRadius: 8, font: 'inherit', fontSize: 13,
                        background: '#fff', color: T.ink,
                        border: `1px solid ${T.line}`,
                      }} />
                  </label>
                ))}
                <span style={{
                  fontSize: 11.5, color: T.inkSoft, lineHeight: 1.5, paddingBottom: 8,
                  flex: 1, minWidth: 160,
                }}>
                  {/*  The years read back, in the same words the name and the library
                       card will use — so what is typed and what is saved cannot look
                       like two different ranges. */}
                  <b>{ageRangeText(draft.ageFrom, draft.ageTo) || 'Type a range'}</b>
                  {draft.ageFrom == null && draft.ageTo != null
                    && <> — everyone up to {draft.ageTo}</>}
                  {draft.ageTo == null && draft.ageFrom != null
                    && <> — no upper limit</>}
                </span>
              </div>
            )}

            {/*  Said plainly, because it is why the lists in the next steps change. */}
            <div style={{ fontSize: 11.5, color: T.inkMuted, marginTop: 7, lineHeight: 1.55 }}>
              {draft.ageBand === 'custom'
                //  A RANGE STILL ANSWERS THE TARIFF QUESTION, and the officer has to know
                //  which answer it gave — a 1–25 range narrows nothing, because the
                //  tariff book splits at 12 and that range holds both kinds of patient.
                ? (() => {
                  const b = check?.template?.effectiveBand;
                  if (b === 'child') return 'Entirely under 12, so the catalogues offer '
                    + 'paediatric tariffs — and a claim outside the range is refused on '
                    + 'the patient\'s actual age.';
                  if (b === 'adult') return 'Entirely 12 and over, so the catalogues offer '
                    + 'adult tariffs — and a claim outside the range is refused on the '
                    + 'patient\'s actual age.';
                  return 'This range holds both children and adults, so no age narrows '
                    + 'the catalogues — the tariff book splits at 12. A claim outside the '
                    + 'range is still refused on the patient\'s actual age.';
                })()
                : draft.ageBand === 'both'
                  ? 'For both bands, so no age narrows the catalogues — an age-specific '
                    + 'tariff picked here would be wrong for half the patients, and is refused.'
                  : draft.ageBand === 'child'
                    ? 'Under 12, so the catalogues offer paediatric tariffs.'
                    : '12 and over, so the catalogues offer adult tariffs.'}
            </div>
          </div>

          <div>
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '.06em',
              textTransform: 'uppercase', color: T.inkFaint, marginBottom: 7,
            }}>Gender</div>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {(vocab.sexes || ['both', 'male', 'female']).map((s) => (
                <Pill key={s} on={draft.sex === s}
                  onClick={() => setDraft((d) => ({ ...d, sex: s }))}>
                  {vocab.sexLabels?.[s] || s.charAt(0).toUpperCase() + s.slice(1)}
                </Pill>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: T.inkMuted, marginTop: 7, lineHeight: 1.55 }}>
              {draft.sex === 'female'
                ? 'Antenatal, postnatal and obstetric entries are available.'
                : 'Antenatal, postnatal and obstetric entries are refused — this '
                  + 'template will be applied to men.'}
            </div>
          </div>

          <div>
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '.06em',
              textTransform: 'uppercase', color: T.inkFaint, marginBottom: 7,
            }}>Status</div>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {/*  Both are SHOWN and both are now open. In-patient was greyed with its
                   reason attached while only the out-patient shape existed; a setting the
                   server closes still greys, which is why `off` reads the server's list
                   rather than a constant here. */}
              {(vocab.settings || ['outpatient', 'inpatient']).map((s) => (
                <Pill key={s} on={draft.setting === s}
                  off={settingsAvailable.includes(s) ? null
                    : (vocab.settingNotes?.[s] || 'Not ready yet')}
                  onClick={() => setDraft((d) => ({
                    ...d,
                    setting: s,
                    //  ── SWITCHING SETTING CLEARS WHAT THE OTHER ONE OWNS ────────
                    //  An out-patient template has no stay and no principal; an
                    //  admission has no attendance and no service. Left behind, each
                    //  would be a value the form still shows and the server drops —
                    //  which is the worst kind of disagreement, because it looks saved.
                    ...(s === 'inpatient'
                      ? { stayDays: d.stayDays || 3, attendance: null, service: null }
                      : {
                        stayDays: null,
                        conditions: d.conditions.map((c) => ({ ...c, is_principal: false })),
                        items: d.items.map((i) => ({ ...i, day: null, is_principal: false })),
                      }),
                  }))}>
                  {vocab.settingLabels?.[s] || s.charAt(0).toUpperCase() + s.slice(1)}
                </Pill>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: T.inkMuted, marginTop: 7, lineHeight: 1.55 }}>
              {draft.setting === 'inpatient'
                ? 'An admission runs over days: every lab and every dose sits on one of '
                  + 'them, and the claim is billed on a procedure or a diagnosis you name.'
                : 'An out-patient template is one visit: the service, what was done and '
                  + 'what was dispensed that day.'}
              {deferred.map((s) => (
                <span key={s} style={{ display: 'block', marginTop: 4, color: T.inkFaint }}>
                  {vocab.settingLabels?.[s] || s}: {vocab.settingNotes?.[s] || 'not ready yet.'}
                </span>
              ))}
            </div>
          </div>

          {/*  ── HOW MANY DAYS ──────────────────────────────────────────────────
               "With the in-patient, we are going to define it with the number of days."
               It comes immediately after the status because everything later hangs off
               it: the day a lab sits on, the day a dose is given, and whether a claim's
               own stay is long enough to take this template at all. */}
          {draft.setting === 'inpatient' && (
            <div>
              <div style={{
                fontSize: 10, fontWeight: 800, letterSpacing: '.06em',
                textTransform: 'uppercase', color: T.inkFaint, marginBottom: 7,
              }}>How many days</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                <input type="number" min={1} max={vocab.stayDaysMax || 60} step={1}
                  value={draft.stayDays ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDraft((d) => {
                      const n = v === '' ? null : Number(v);
                      return {
                        ...d,
                        stayDays: n,
                        //  ── A SHORTER STAY CANNOT ORPHAN A DOSE ──────────────
                        //  Dropping the stay from 5 to 3 would leave drugs on days 4
                        //  and 5 that the server refuses — and the officer would meet
                        //  that as a save error with no idea which rows. They are
                        //  pulled back to the last day instead, visibly, because the
                        //  template pane regroups in front of them.
                        items: n ? d.items.map((i) => (
                          i.day && i.day > n ? { ...i, day: n } : i
                        )) : d.items,
                      };
                    });
                  }}
                  style={{
                    width: 92, boxSizing: 'border-box', padding: '9px 11px',
                    borderRadius: 8, font: 'inherit', fontSize: 13.5, fontWeight: 700,
                    background: '#fff', color: T.ink, border: `1px solid ${T.line}`,
                  }} />
                {/*  The common stays, one click each. Three days is the commonest
                     admission on this data and typing it every time is friction. */}
                {[1, 2, 3, 5, 7].map((n) => (
                  <Pill key={n} on={draft.stayDays === n}
                    onClick={() => setDraft((d) => ({
                      ...d, stayDays: n,
                      items: d.items.map((i) => (i.day && i.day > n ? { ...i, day: n } : i)),
                    }))}>{n === 1 ? '1 day' : `${n} days`}</Pill>
                ))}
              </div>
              <div style={{ fontSize: 11.5, color: T.inkMuted, marginTop: 7, lineHeight: 1.55 }}>
                {draft.stayDays
                  ? <>Day 1 is the day of admission. A claim that stayed fewer than
                      {' '}<b>{draft.stayDays}</b> days is refused — the last doses would fall
                      after discharge.</>
                  : 'Required. The labs and the drugs are placed on these days.'}
              </div>
            </div>
          )}

          {/*  ── THE VISIT ITSELF — OUT-PATIENT ONLY ────────────────────────────
               The attendance BEFORE the service, because it is how an officer thinks
               about the visit and because the service list is already narrowed by the
               age band above it. Both optional: a template that only says what to give
               is still a useful template, and demanding a service to save one would
               make the screen harder for nothing.

               NEITHER APPEARS ON AN ADMISSION. "The type of attendance will not show up,
               because with the in-patient it is either the diagnosis or the procedure
               that becomes the principal G-DRG already."

               The same reasoning retires the SERVICE: the four attendance codes describe
               an OPD visit, and an admission is billed on what was DONE — the procedure
               or the diagnosis the officer stars two steps down. Offering a service field
               here would invite a third answer to a question already settled.

               HIDDEN, not merely ignored: the server holds both to null on an admission,
               so a field left on screen would show a value that is not being saved. */}
          {draft.setting !== 'inpatient' && (
          <>
          <div style={{ borderTop: `1px solid ${T.line}`, paddingTop: 16 }}>
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '.06em',
              textTransform: 'uppercase', color: T.inkFaint, marginBottom: 7,
            }}>Type of attendance</div>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {(vocab.attendances || ['EAE', 'CFU', 'ANC', 'PNC']).map((a) => {
                //  ANC and PNC are women's care. Closed with the reason on a male or
                //  either-sex template, rather than offered and then refused on save —
                //  the same list the endpoint refuses from, sent with the vocabulary.
                const womenOnly = (vocab.femaleOnlyAttendances || []).includes(a);
                const off = womenOnly && draft.sex !== 'female'
                  ? `${vocab.attendanceLabels?.[a] || a} is women's care, and this `
                    + `template is for ${draft.sex === 'male' ? 'male patients' : 'either sex'}. `
                    + 'Set the gender to Female to use it.'
                  : null;
                return (
                  <Pill key={a} on={draft.attendance === a} off={off}
                    onClick={() => setDraft((d) => ({
                      ...d, attendance: d.attendance === a ? null : a,
                    }))}>
                    {vocab.attendanceLabels?.[a] || a}
                  </Pill>
                );
              })}
            </div>
            <div style={{ fontSize: 11.5, color: T.inkMuted, marginTop: 7, lineHeight: 1.55 }}>
              {draft.attendance
                ? <>Every claim this is applied to becomes
                    {' '}<b>{vocab.attendanceLabels?.[draft.attendance] || draft.attendance}</b>
                    {' '}({draft.attendance}).</>
                : 'Optional — leave it unset and each claim keeps the attendance it has.'}
            </div>
          </div>

          <div>
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '.06em',
              textTransform: 'uppercase', color: T.inkFaint, marginBottom: 7,
            }}>Service</div>
            {/*  A SELECT, not a searchable list: this catalogue is 35 rows, not 594, and
                 the claim editor offers it the same way. Consistency with the screen the
                 officer already knows beats a second interaction pattern. */}
            <select
              value={draft.service ? draft.service.code : ''}
              onChange={(e) => {
                const code = e.target.value;
                if (!code) { setDraft((d) => ({ ...d, service: null })); return; }
                const o = services.options.find((x) => String(x.code) === code);
                setDraft((d) => ({
                  ...d,
                  service: o
                    ? { code: o.code, name: o.name || o.label || o.code, tariff: o.amount ?? null }
                    : null,
                }));
              }}
              style={{
                width: '100%', maxWidth: 460, boxSizing: 'border-box',
                padding: '9px 11px', borderRadius: 8, font: 'inherit', fontSize: 12.5,
                background: '#fff', color: T.ink,
                border: `1px solid ${serviceStale ? T.caution : T.line}`,
              }}>
              <option value="">
                {services.loading ? 'Loading the services…' : 'No service — leave the claim\'s own'}
              </option>
              {services.options.map((o) => (
                <option key={o.code} value={o.code}>
                  {(o.label || o.name)}{o.code ? ` — ${o.code}` : ''}
                  {o.amount != null ? `  ·  ${Number(o.amount).toFixed(2)}` : ''}
                </option>
              ))}
            </select>
            <div style={{ fontSize: 11.5, color: T.inkMuted, marginTop: 7, lineHeight: 1.55 }}>
              {/*  Says WHY it matters on an out-patient claim, because that is not
                   obvious and it is the whole reason this field was asked for. */}
              {serviceStale
                ? <span style={{ color: T.caution, fontWeight: 700 }}>
                    {draft.service.name} ({draft.service.code}) is not in the list for this
                    age band any more. Pick one that is, or the save will be refused.
                  </span>
                : draft.service
                  ? <>On an out-patient claim the service is what the whole claim is billed
                      on, so every claim this is applied to becomes
                      {' '}<b>{draft.service.code}</b>.</>
                  : 'Optional. On an out-patient claim this is the code the claim is billed '
                    + 'on — set it and the template finishes the claim rather than only '
                    + 'filling in the treatment.'}
              {services.note && (
                <span style={{ display: 'block', marginTop: 4, color: T.inkFaint }}>
                  {services.note}
                </span>
              )}
            </div>
          </div>
          </>
          )}
        </div>
      );
    }
    const k = KINDS.find((x) => x.key === effectiveStep);
    const days = byDay && draft.stayDays
      ? Array.from({ length: draft.stayDays }, (_, i) => i + 1) : [];
    const onDay = (n) => draft.items.filter((i) => i.kind === k.key && i.day === n).length;
    return (
      <>
        <div style={{ fontSize: 12.5, color: T.inkMuted, lineHeight: 1.6, marginBottom: 10 }}>
          {k.hint}
        </div>

        {/*  ── THE DAY BEING FILLED IN ────────────────────────────────────────
             "These labs must be given on the first day, second day, third day… you get
             to the medication: first day the drugs, second day the drugs, third day the
             drugs."

             A day is CHOSEN and then things are picked into it, rather than each row
             carrying a day dropdown after the fact. That is how a drug chart is written
             on paper, and it is the only arrangement in which the same drug can be added
             three times — the picker's tick is keyed on the day, so Paracetamol reads as
             un-picked on day 2 after being added to day 1. */}
        {days.length > 0 && (
          <div style={{
            border: `1px solid ${T.green}33`, background: T.greenSoft, borderRadius: 10,
            padding: '10px 12px', marginBottom: 11,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            }}>
              <span style={{
                fontSize: 10, fontWeight: 800, letterSpacing: '.06em',
                textTransform: 'uppercase', color: T.inkFaint,
              }}>Day</span>
              {days.map((n) => {
                const on = activeDay === n;
                const has = onDay(n);
                return (
                  <button key={n} type="button" onClick={() => setActiveDay(n)}
                    title={`Day ${n}${has ? ` — ${has} already` : ''}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '6px 11px', borderRadius: 999, font: 'inherit',
                      fontSize: 12, fontWeight: on ? 800 : 650, cursor: 'pointer',
                      border: `1px solid ${on ? T.green : T.line}`,
                      background: on ? T.green : '#fff',
                      color: on ? '#fff' : T.inkSoft,
                    }}>
                    {n}
                    {/*  A count on the pill, so an unfinished day is visible without
                         opening it. A chart with nothing on day 3 is the commonest
                         mistake this screen can make. */}
                    {has > 0 && (
                      <span style={{
                        ...MONO, fontSize: 10, fontWeight: 800,
                        color: on ? '#fff' : T.green, opacity: on ? 0.9 : 1,
                      }}>· {has}</span>
                    )}
                  </button>
                );
              })}
              {/*  ── REPEAT THIS DAY ACROSS THE STAY ──────────────────────────
                   A three-day course of the same four drugs is the ordinary case, and
                   picking them three times is twelve clicks to say one thing. This
                   copies the day in front of the officer onto every other day, skipping
                   any that already has that entry — so pressing it twice does not
                   double anything. */}
              {onDay(activeDay) > 0 && days.length > 1 && (
                <button type="button"
                  onClick={() => setDraft((d) => {
                    const src = d.items.filter((i) => i.kind === k.key && i.day === activeDay);
                    const extra = [];
                    for (const n of days) {
                      if (n === activeDay) continue;
                      for (const it of src) {
                        const exists = d.items.some((x) => x.kind === k.key
                          && x.day === n && x.code === it.code);
                        if (!exists) extra.push({ ...it, day: n, is_principal: false });
                      }
                    }
                    return { ...d, items: [...d.items, ...extra] };
                  })}
                  style={{
                    marginLeft: 'auto', display: 'inline-flex', alignItems: 'center',
                    gap: 6, padding: '6px 11px', borderRadius: 8, font: 'inherit',
                    fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                    border: `1px solid ${T.green}66`, background: '#fff', color: T.green,
                  }}>
                  <Copy size={12} />
                  Repeat day {activeDay} on all {days.length} days
                </button>
              )}
            </div>
            <div style={{ fontSize: 11.5, color: T.inkSoft, marginTop: 7, lineHeight: 1.5 }}>
              Picking below adds to <b>day {activeDay}</b>
              {activeDay === 1 ? ' — the day of admission' : ''}.
              {' '}{onDay(activeDay) === 0
                ? `Nothing on day ${activeDay} yet.`
                : `${onDay(activeDay)} on this day.`}
            </div>
          </div>
        )}

        <Catalogue
          url={k.url}
          params={{ age: ageForBand(effBand) }}
          placeholder={`Search ${k.one}…`}
          chosenIds={chosenIds}
          rowLabel={LABELS[k.key]}
          //  The tick answers "is this on the day I am filling in", not "is this
          //  anywhere on the template".
          keyOf={(kind, row) => itemKey(kind, row, byDay ? activeDay : null)}
          onPick={(row) => addItem(k.key, row)}
          tall={k.key !== 'medication'} />
        {/*  The prescriptions, under the picker that adds them. On the medication step
             only, because that is the only step whose additions need finishing — and
             putting them in the template pane would make that pane a form. */}
        {k.key === 'medication' && countOf('medication') > 0 && (
          <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '.06em',
              textTransform: 'uppercase', color: T.inkFaint,
            }}>
              {/*  THE DAY BEING WORKED ON, not the whole chart. On a three-day stay the
                   full list is the same four drugs three times, and finding the row you
                   just added would mean reading twelve near-identical cards. The day
                   strip above is the context; this follows it. */}
              The prescriptions{byDay ? ` · day ${activeDay}` : ''}
            </div>
            {draft.items
              .filter((i) => i.kind === 'medication'
                && (!byDay || (i.day ?? 1) === activeDay))
              .map((it) => {
              const gaps = RX_FIELDS.filter(([f]) => !String(it[f] ?? '').trim()).length;
              return (
                //  The DAY is in the key. Without it a drug repeated across days gives
                //  React three identical keys, and editing one card writes into another.
                <div key={`${it.code}@${it.day ?? '-'}`} style={{
                  border: `1px solid ${gaps ? `${T.caution}55` : T.line}`,
                  borderRadius: 9, padding: '9px 11px',
                  background: gaps ? T.cautionSoft : '#fff',
                }}>
                  <div style={{
                    display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 7,
                    minWidth: 0,
                  }}>
                    <span style={{
                      flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: T.ink,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{it.name}</span>
                    {gaps > 0 && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: T.caution }}>
                        {gaps} field{gaps === 1 ? '' : 's'} to fill
                      </span>
                    )}
                    <button type="button" onClick={() => dropItem('medication', it.code, it.day)}
                      aria-label={`Remove ${it.name}`}
                      style={{
                        border: 0, background: 'none', cursor: 'pointer',
                        color: T.inkFaint, padding: 2, lineHeight: 1,
                      }}><X size={13} /></button>
                  </div>
                  <div style={{
                    display: 'grid', gap: 7,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))',
                  }}>
                    {RX_FIELDS.map(([f, label, ph]) => {
                      const bad = !String(it[f] ?? '').trim();
                      const opts = f === 'frequency' ? rxVocab.frequencies
                        : f === 'route' ? rxVocab.routes : null;
                      return (
                        <label key={f} style={{ display: 'grid', gap: 3, minWidth: 0 }}>
                          <span style={{
                            fontSize: 9, fontWeight: 800, letterSpacing: '.05em',
                            textTransform: 'uppercase',
                            color: bad ? T.danger : T.inkFaint,
                          }}>
                            {label}
                            {f === 'quantity' && it.quantityAuto && !it.quantityTyped && (
                              <span style={{ color: T.green, marginLeft: 5 }}>worked out</span>
                            )}
                          </span>
                          {opts && opts.length ? (
                            <select value={it[f] || ''}
                              onChange={(e) => patchItem(it.kind, it.code, f, e.target.value, it.day)}
                              style={{
                                padding: '6px 8px', borderRadius: 7, font: 'inherit',
                                fontSize: 11.5, minWidth: 0, background: '#fff',
                                border: `1px solid ${bad ? T.danger : T.line}`, color: T.ink,
                              }}>
                              <option value="">— {label.toLowerCase()} —</option>
                              {opts.map((o) => {
                                const v = typeof o === 'string' ? o : (o.value ?? '');
                                const l = typeof o === 'string' ? o : (o.label ?? o.value ?? '');
                                return <option key={v} value={v}>{l}</option>;
                              })}
                            </select>
                          ) : (
                            <input value={it[f] || ''} placeholder={ph}
                              onChange={(e) => patchItem(it.kind, it.code, f, e.target.value, it.day)}
                              style={{
                                padding: '6px 8px', borderRadius: 7, font: 'inherit',
                                fontSize: 11.5, minWidth: 0, background: '#fff',
                                border: `1px solid ${bad ? T.danger : T.line}`, color: T.ink,
                              }} />
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  })();

  // ── The template pane ─────────────────────────────────────────────────────
  const templatePane = (
    <div style={{
      background: '#fff', border: `1px solid ${T.line}`, borderRadius: 11,
      display: 'flex', flexDirection: 'column', minWidth: 0,
      //  Sticky on a wide screen: it is the thing being made, and scrolling the picker
      //  must not scroll away what the picker is filling.
      ...(wide ? { position: 'sticky', top: 12, maxHeight: 'calc(100vh - 48px)' } : {}),
    }}>
      <div style={{
        padding: '11px 13px', borderBottom: `1px solid ${T.line}`, flexShrink: 0,
      }}>
        <div style={{
          fontSize: 10, fontWeight: 800, letterSpacing: '.06em',
          textTransform: 'uppercase', color: T.inkSoft, marginBottom: 7,
        }}>This template</div>
        <input value={shownName}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          placeholder="Pick a condition and the name writes itself"
          style={{
            width: '100%', boxSizing: 'border-box', padding: '9px 11px',
            borderRadius: 8, font: 'inherit', fontSize: 13.5, background: '#fff',
            border: `1px solid ${T.line}`, color: T.ink, fontWeight: 750,
          }} />
        <div style={{ marginTop: 5, minHeight: 16 }}>
          {!draft.name && shownName ? (
            <span style={{ fontSize: 11, color: T.green, fontWeight: 700 }}>
              Suggested — type over it if you prefer
            </span>
          ) : draft.name && check?.template?.suggestedName
            && draft.name !== check.template.suggestedName ? (
              <button type="button" onClick={() => setDraft((d) => ({ ...d, name: '' }))}
                style={{
                  border: 0, background: 'none', padding: 0, font: 'inherit',
                  fontSize: 11, fontWeight: 700, cursor: 'pointer', color: T.green,
                  textDecoration: 'underline', textAlign: 'left',
                }}>
                Use “{check.template.suggestedName}” instead
              </button>
            ) : null}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px 13px 10px' }}>
        {!hasCondition ? (
          <div style={{
            fontSize: 12.5, color: T.inkMuted, lineHeight: 1.7, padding: '14px 0',
          }}>
            {/*  Says where they are in the sequence, not just that the pane is empty.
                 Step 1 is already answered — it has defaults — so the instruction is
                 step 2, and the answers to step 1 are read back so that default is a
                 visible choice rather than a silent one. */}
            For <b>{whoSummary}</b>. Now pick <b>The condition</b> on the left —
            what to give follows from it.
          </div>
        ) : (
          <>
            {[['Condition', draft.conditions.map((c) => ({
              //  Keyed and removed on the PAIR, like everything else that identifies a
              //  diagnosis here. Two B54 rows cannot both be on a template, so the
              //  ICD-10 alone would work today — but a key that is right only because of
              //  a rule enforced elsewhere breaks the day that rule moves.
              key: idOf('diagnosis', c),
              code: [c.icd10_code, c.gdrg_code].filter(Boolean).join(' · '),
              name: c.name,
              day: null,
              //  ── THE STAR, ON CONDITIONS ONLY WHEN THERE IS NO PROCEDURE ────
              //  "If there is no procedure, you let the person specify which one is the
              //  principal G-DRG for the condition." On an admission a procedure
              //  outranks every diagnosis, so once one exists the choice has moved and
              //  a star here would be a control the rule ignores — worse than none.
              star: byDay && countOf('procedure') === 0 ? {
                on: !!c.is_principal,
                onToggle: () => setDraft((d) => ({
                  ...d,
                  //  EXCLUSIVE, like the procedure star: one principal per claim.
                  conditions: d.conditions.map((x) => ({
                    ...x, is_principal: x.icd10_code === c.icd10_code,
                  })),
                })),
              } : null,
              onRemove: () => setDraft((d) => ({
                ...d,
                conditions: d.conditions.filter(
                  (x) => idOf('diagnosis', x) !== idOf('diagnosis', c)
                ),
              })),
            }))],
            ...KINDS.map((k) => [k.label, draft.items.filter((i) => i.kind === k.key)
              //  ── BY DAY, THEN BY THE ORDER THEY WERE PICKED ──────────────────
              //  So a chart reads down the stay rather than in the order the officer
              //  happened to jump between days.
              .slice()
              .sort((a, b) => (byDay ? (a.day || 1) - (b.day || 1) : 0))
              .map((it) => ({
                //  The DAY is in the key, or a drug repeated across days gives React
                //  identical keys and the rows stop tracking their own data.
                key: `${it.code}@${it.day ?? '-'}`,
                code: it.code,
                name: it.name,
                day: it.day ?? null,
                detail: it.kind === 'medication'
                  ? ([it.dose, it.frequency,
                    it.duration ? `${it.duration} day(s)` : null, it.route,
                    it.quantity ? `qty ${it.quantity}` : null].filter(Boolean).join(' · ')
                    || 'no prescription yet')
                  : null,
                tone: it.kind === 'medication'
                  && !RX_FIELDS.every(([f]) => String(it[f] ?? '').trim())
                  ? T.caution : null,
                //  ── THE STAR, ON PROCEDURES ONLY ─────────────────────────────
                //  "If you are building a procedure, you have to let the person specify
                //  which will be the principal." Offered only on an admission's
                //  procedures — a lab or a drug is never what a claim is billed on, and
                //  a star there would be a control that means nothing.
                star: byDay && k.key === 'procedure' ? {
                  on: !!it.is_principal,
                  onToggle: () => setDraft((d) => ({
                    ...d,
                    items: d.items.map((x) => (x.kind === 'procedure'
                      //  EXCLUSIVE. One principal per claim, so starring one un-stars
                      //  the rest rather than leaving the officer to notice they have
                      //  two and be refused on save.
                      ? { ...x, is_principal: x.code === it.code && (x.day ?? null) === (it.day ?? null) }
                      : x)),
                    //  A procedure now owns the principal, so no diagnosis may keep it.
                    conditions: d.conditions.map((c) => ({ ...c, is_principal: false })),
                  })),
                } : null,
                onRemove: () => dropItem(k.key, it.code, it.day),
              }))])]
              .map(([label, rows]) => {
                if (!rows.length) return null;
                //  On an admission the rows are broken under a day heading, because
                //  "day 2" is the question the officer is answering when they read this.
                let lastDay = null;
                return (
                  <div key={label} style={{ marginTop: 12, minWidth: 0 }}>
                    <div style={{
                      fontSize: 9.5, fontWeight: 800, letterSpacing: '.06em',
                      textTransform: 'uppercase', color: T.inkFaint,
                    }}>{label} · {rows.length}</div>
                    {rows.map((r) => {
                      const head = byDay && r.day != null && r.day !== lastDay;
                      if (head) lastDay = r.day;
                      return (
                        <React.Fragment key={r.key}>
                          {head && (
                            <div style={{
                              fontSize: 9.5, fontWeight: 800, color: T.green,
                              marginTop: 6, letterSpacing: '.04em',
                            }}>DAY {r.day}</div>
                          )}
                          <Line code={r.code} name={r.name} detail={r.detail}
                            tone={r.tone} onRemove={r.onRemove} star={r.star}
                            removeLabel={`Remove ${r.name}`} />
                        </React.Fragment>
                      );
                    })}
                  </div>
                );
              })}

            {/*  Who it is for, at the foot of the list rather than the top: it is the
                 qualifier on everything above, and it is also in the name. */}
            <div style={{
              marginTop: 14, paddingTop: 10, borderTop: `1px solid ${T.line}`,
              display: 'flex', gap: 6, flexWrap: 'wrap',
            }}>
              {/*  The visit fields join only when SET — they are optional, and a chip
                   reading "no service" is worse than no chip. */}
              {[vocab.ageLabels?.[draft.ageBand] || draft.ageBand,
                vocab.sexLabels?.[draft.sex] || draft.sex,
                vocab.settingLabels?.[draft.setting] || draft.setting,
                draft.attendance
                  ? (vocab.attendanceLabels?.[draft.attendance] || draft.attendance) : null,
                draft.service ? draft.service.code : null,
              ].filter(Boolean).map((label, i) => (
                <span key={i} style={{
                  padding: '3px 9px', borderRadius: 999, fontSize: 10.5, fontWeight: 700,
                  background: T.head, color: T.inkMuted, border: `1px solid ${T.line}`,
                }}>{label}</span>
              ))}
            </div>
          </>
        )}
      </div>

      {/*  ── THE REFUSALS, WHERE SAVE IS ─────────────────────────────────────
           Beside the button they block rather than higher up the page, so the reason
           and the dead button are read together. */}
      {conflicts.length > 0 && (
        <div style={{
          padding: '10px 13px', borderTop: `1px solid ${T.line}`,
          background: T.dangerSoft, display: 'grid', gap: 6, flexShrink: 0,
          maxHeight: 190, overflowY: 'auto',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5,
            fontWeight: 800, color: T.danger,
          }}>
            <AlertTriangle size={13} />
            {conflicts.length === 1 ? 'One entry does not suit this patient'
              : `${conflicts.length} entries do not suit this patient`}
          </div>
          {conflicts.map((c) => (
            <div key={`${c.kind}-${c.code}`}
              style={{ fontSize: 11.5, color: T.danger, lineHeight: 1.5 }}>{c.why}</div>
          ))}
        </div>
      )}
      {(check?.warnings || []).length > 0 && conflicts.length === 0 && (
        <div style={{
          padding: '9px 13px', borderTop: `1px solid ${T.line}`,
          background: T.cautionSoft, fontSize: 11.5, color: T.caution, lineHeight: 1.55,
          flexShrink: 0, maxHeight: 150, overflowY: 'auto',
        }}>
          {check.warnings.map((w) => <div key={w}>{w}</div>)}
          {/*  A warning may be saved. Said, so nobody hunts for a blocked button that is
               not blocked. */}
          <div style={{ marginTop: 5, fontWeight: 700 }}>
            These can be finished later — it will still save.
          </div>
        </div>
      )}

      <div style={{
        padding: '11px 13px', borderTop: `1px solid ${T.line}`, flexShrink: 0,
        display: 'flex', gap: 8,
      }}>
        <button type="button" onClick={closeBuilder}
          style={{
            padding: '9px 13px', borderRadius: 8, cursor: 'pointer', font: 'inherit',
            fontSize: 12.5, fontWeight: 700, background: '#fff',
            border: `1px solid ${T.line}`, color: T.inkSoft,
          }}>Cancel</button>
        <button type="button" disabled={!canSave} onClick={save}
          title={!hasCondition ? 'Pick a condition first'
            : conflicts.length ? 'Fix the refusals above first' : undefined}
          style={{
            flex: 1, display: 'inline-flex', alignItems: 'center', gap: 7,
            justifyContent: 'center', padding: '10px 16px', borderRadius: 8,
            fontSize: 12.5, fontWeight: 800, fontFamily: 'inherit',
            border: '1px solid transparent', color: '#fff',
            cursor: canSave ? 'pointer' : 'not-allowed',
            background: canSave ? T.green : T.inkFaint,
          }}>
          <Save size={14} />
          {saving ? 'Saving…' : draft.id ? 'Save changes' : 'Save template'}
        </button>
      </div>
    </div>
  );

  // ── The rail ──────────────────────────────────────────────────────────────
  const rail = (
    <div style={{
      display: medium ? 'grid' : 'flex',
      gap: medium ? 4 : 6,
      alignContent: 'start',
      overflowX: medium ? 'visible' : 'auto',
      paddingBottom: medium ? 0 : 4,
      minWidth: 0,
    }}>
      {STEPS.map((s) => {
        const on = effectiveStep === s.key;
        const Icon = s.icon;
        return (
          <button key={s.key} type="button"
            disabled={!!s.locked}
            title={s.locked || undefined}
            onClick={() => !s.locked && setStep(s.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 9, minWidth: medium ? 0 : 150,
              textAlign: 'left', font: 'inherit', padding: '9px 11px', borderRadius: 9,
              cursor: s.locked ? 'not-allowed' : 'pointer',
              border: `1px solid ${on ? T.green : 'transparent'}`,
              background: on ? T.greenSoft : 'transparent',
              opacity: s.locked ? 0.5 : 1,
              flexShrink: 0,
            }}>
            {/*  The number becomes a TICK once the step is answered. Progress you can
                 see without reading — which is the point of a rail. */}
            <span style={{
              flexShrink: 0, width: 22, height: 22, borderRadius: 6, display: 'grid',
              placeItems: 'center', fontSize: 10.5, fontWeight: 800,
              background: s.done ? T.green : (on ? '#fff' : T.head),
              color: s.done ? '#fff' : (on ? T.green : T.inkMuted),
              border: `1px solid ${s.done ? T.green : (on ? T.green : T.line)}`,
            }}>
              {s.done ? <Check size={12} /> : s.n}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{
                display: 'block', fontSize: 12.5, fontWeight: on ? 800 : 650,
                color: on ? T.green : T.inkSoft,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{s.label}</span>
              {/*  Step 1 reads its three answers back here, in place of a count it can
                   never have. Ellipsised rather than wrapped, so the rail rows stay one
                   height whatever the words are. */}
              {s.sub && (
                <span style={{
                  display: 'block', fontSize: 10.5, fontWeight: 600, marginTop: 1,
                  color: on ? T.green : T.inkFaint, opacity: on ? 0.85 : 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{s.sub}</span>
              )}
            </span>
            {s.count > 0 && (
              <span style={{
                flexShrink: 0, fontSize: 10.5, fontWeight: 800, ...MONO,
                color: on ? T.green : T.inkFaint,
              }}>{s.count}</span>
            )}
            <Icon size={13} style={{ flexShrink: 0, opacity: 0.55 }}
              color={on ? T.green : T.inkFaint} />
          </button>
        );
      })}
    </div>
  );

  return (
    <div style={{ minHeight: '100%', background: T.canvas, padding: '14px 16px 28px', color: T.ink }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, letterSpacing: '-.01em' }}>
          Condition Templates
        </h2>
        <span style={{ fontSize: 13, color: T.inkMuted, lineHeight: 1.6 }}>
          For a condition, in a kind of patient: the labs, drugs and procedures that go
          with it. Built from your own priced catalogues.
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button type="button" onClick={load} title="Read them again"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px',
              borderRadius: 8, cursor: 'pointer', font: 'inherit', fontSize: 12,
              fontWeight: 700, background: '#fff', border: `1px solid ${T.line}`,
              color: T.inkSoft,
            }}>
            <RefreshCw size={13} /> Refresh
          </button>
          {!building && (
            <button type="button" onClick={startNew}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                borderRadius: 8, cursor: 'pointer', font: 'inherit', fontSize: 12.5,
                fontWeight: 800, background: T.green, border: '1px solid transparent',
                color: '#fff',
              }}>
              <Plus size={14} /> New template
            </button>
          )}
        </span>
      </div>

      {loadErr && (
        <div style={{
          padding: '10px 13px', borderRadius: 8, marginBottom: 12, fontSize: 12.5,
          background: T.dangerSoft, border: `1px solid ${T.danger}44`, color: T.danger,
        }}>{loadErr}</div>
      )}

      {/*  ══ THE BUILDER — THREE PANES ══════════════════════════════════════ */}
      {building && (
        <div style={{
          background: '#fff', border: `1px solid ${T.green}55`, borderRadius: 12,
          marginBottom: 18, overflow: 'hidden',
        }}>
          <div style={{
            padding: '11px 16px', background: T.greenSoft,
            borderBottom: `1px solid ${T.line}`, display: 'flex', alignItems: 'center',
            gap: 10, flexWrap: 'wrap',
          }}>
            <span style={{
              fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em',
              textTransform: 'uppercase', color: T.green,
            }}>{draft.id ? 'Editing a template' : 'A new template'}</span>
            <span style={{ fontSize: 12, color: T.inkMuted }}>
              Nothing is saved until you press Save.
            </span>
            <button type="button" onClick={closeBuilder} aria-label="Close the builder"
              style={{
                marginLeft: 'auto', border: `1px solid ${T.line}`, background: '#fff',
                width: 26, height: 26, borderRadius: 7, display: 'grid',
                placeItems: 'center', cursor: 'pointer', color: T.inkMuted,
              }}><X size={13} /></button>
          </div>

          <div style={{
            display: 'grid',
            //  Three panes wide; rail plus pane on a tablet with the template beneath;
            //  one column on a phone with the rail as scrolling tabs.
            gridTemplateColumns: wide ? '218px minmax(0, 1fr) 340px'
              : medium ? '206px minmax(0, 1fr)' : 'minmax(0, 1fr)',
            gap: 0,
            alignItems: 'stretch',
          }}>
            <div style={{
              padding: medium ? '14px 10px' : '12px 14px 0',
              borderRight: medium ? `1px solid ${T.line}` : 'none',
              borderBottom: medium ? 'none' : `1px solid ${T.line}`,
              background: T.canvas, minWidth: 0,
            }}>
              {medium && (
                <div style={{
                  fontSize: 9.5, fontWeight: 800, letterSpacing: '.06em',
                  textTransform: 'uppercase', color: T.inkFaint,
                  padding: '0 11px 8px',
                }}>Steps</div>
              )}
              {rail}
            </div>

            <div style={{
              padding: 16, minWidth: 0, display: 'flex', flexDirection: 'column',
              borderRight: wide ? `1px solid ${T.line}` : 'none',
              borderBottom: wide ? 'none' : `1px solid ${T.line}`,
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4,
              }}>
                <span style={{
                  fontSize: 13.5, fontWeight: 800, color: T.ink,
                }}>
                  {activeStep.n}. {activeStep.label}
                </span>
              </div>
              <div style={{ flex: 1, minHeight: 0, minWidth: 0 }}>{pane}</div>
            </div>

            <div style={{ padding: wide ? 12 : 16, minWidth: 0, background: T.canvas }}>
              {templatePane}
            </div>
          </div>
        </div>
      )}

      {/*  ══ THE LIBRARY ═══════════════════════════════════════════════════ */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em',
          textTransform: 'uppercase', color: T.inkSoft,
        }}>Saved templates</span>
        {/*  SHOWN OF TOTAL while a tab or a search narrows it, so the number beside
             the heading can never read as the whole library when it is not. */}
        <span style={{ fontSize: 12, color: T.inkFaint }}>
          {shown.length === (list || []).length
            ? (list || []).length.toLocaleString()
            : `${shown.length.toLocaleString()} of ${(list || []).length.toLocaleString()}`}
        </span>
        <div style={{ position: 'relative', flex: 1, minWidth: 220, maxWidth: 420 }}>
          <Search size={13} style={{
            position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
            color: T.inkFaint, pointerEvents: 'none',
          }} />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, condition or anything inside…"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '7px 10px 7px 27px',
              borderRadius: 8, font: 'inherit', fontSize: 12.5, background: '#fff',
              border: `1px solid ${T.line}`, color: T.ink,
            }} />
        </div>

        {/*  ── OUT-PATIENT OR IN-PATIENT ────────────────────────────────────────
             One click, one answer. NOT DISABLED AT ZERO: a tab that cannot be
             pressed leaves the officer guessing whether there are none or whether
             the screen is broken, and the empty state below says which in words.
             `flexWrap` on the row above lets this drop under the search box on a
             phone rather than squeezing it. */}
        <div role="group" aria-label="Show which templates"
          style={{
            display: 'inline-flex', flexShrink: 0, background: T.head,
            border: `1px solid ${T.line}`, borderRadius: 9, padding: 2, gap: 2,
          }}>
          {[['all', 'All'], ['outpatient', 'Out-patient'], ['inpatient', 'In-patient']]
            .map(([key, label]) => {
              const on = setting === key;
              const n = settingCounts[key];
              return (
                <button key={key} type="button" aria-pressed={on}
                  onClick={() => setSetting(key)}
                  title={n === 0
                    ? `No ${label.toLowerCase()} templates${q.trim() ? ' match this search' : ' yet'}`
                    : `Show the ${n} ${label.toLowerCase()} template${n === 1 ? '' : 's'}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '5px 11px', borderRadius: 7, font: 'inherit',
                    fontSize: 11.5, fontWeight: on ? 800 : 650, cursor: 'pointer',
                    border: `1px solid ${on ? `${T.green}33` : 'transparent'}`,
                    background: on ? '#fff' : 'transparent',
                    color: on ? T.green : T.inkMuted,
                  }}>
                  {label}
                  <span style={{
                    ...MONO, fontSize: 10.5, fontWeight: 700,
                    color: on ? T.green : T.inkFaint,
                  }}>{n}</span>
                </button>
              );
            })}
        </div>
      </div>

      {list === null ? (
        <div style={{ fontSize: 13, color: T.inkMuted }}>Reading the templates…</div>
      ) : shown.length === 0 ? (
        <div style={{
          background: '#fff', border: `1px solid ${T.line}`, borderRadius: 12,
          padding: '28px 20px', textAlign: 'center',
        }}>
          {/*  THREE DIFFERENT EMPTIES, and they need three different sentences. An
               empty library is an invitation; an empty tab is a fact about the tab,
               and telling somebody to "try a different search" when they have typed
               nothing and only pressed In-patient sends them looking for a fault in
               the search box. Each says which of the two narrowed it, and offers the
               way back. */}
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
            {(list || []).length === 0
              ? 'No templates yet'
              : setting === 'inpatient' && !q.trim()
                ? 'No in-patient templates yet'
                : setting === 'outpatient' && !q.trim()
                  ? 'No out-patient templates yet'
                  : 'Nothing here matches'}
          </div>
          <div style={{ fontSize: 12.5, color: T.inkMuted, lineHeight: 1.7 }}>
            {(list || []).length === 0 ? (
              <>Press <b>New template</b>, pick a condition, say who it is for, and add
                the labs and drugs that go with it. The name writes itself.</>
            ) : !q.trim() ? (
              <>
                Every template you have is
                {' '}{setting === 'inpatient' ? 'out-patient' : 'in-patient'}.
                {' '}Press <b>New template</b> and choose
                {' '}<b>{setting === 'inpatient' ? 'In-patient' : 'Out-patient'}</b> at the
                {' '}patient step to build one, or press <b>All</b> to see them again.
              </>
            ) : setting === 'all' ? (
              <>Try a different search.</>
            ) : (
              <>
                Nothing {setting === 'inpatient' ? 'in-patient' : 'out-patient'} matches
                {' '}<b>{q.trim()}</b>
                {settingCounts.all > 0
                  ? <>, though {settingCounts.all} other template
                      {settingCounts.all === 1 ? '' : 's'} do. Press <b>All</b> to
                      see {settingCounts.all === 1 ? 'it' : 'them'}.</>
                  : <>. Try a different search.</>}
              </>
            )}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {shown.map((t) => {
            const isOpen = open.has(t.id);
            return (
              <div key={t.id} style={{
                background: '#fff', border: `1px solid ${T.line}`, borderRadius: 12,
                minWidth: 0,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px',
                  flexWrap: 'wrap', minWidth: 0,
                }}>
                  <span style={{ flex: 1, minWidth: 200 }}>
                    <span style={{
                      display: 'block', fontSize: 13.5, fontWeight: 750, color: T.ink,
                    }}>{t.name}</span>
                    <span style={{
                      display: 'block', fontSize: 11.5, color: T.inkMuted, marginTop: 2,
                    }}>
                      {t.conditions.map((c) => c.name).join(' + ') || '—'}
                      {t.actor ? ` · by ${t.actor}` : ''}
                    </span>
                  </span>

                  {/*  All three axes, always shown — including 'Both'. On a ROW they are
                       what tells two templates for the same condition apart, which is
                       the opposite of the name, where a Both says nothing.
                       Highlighted only when the value narrows: a 'Both' is present so the
                       row is complete, not because it distinguishes anything. */}
                  {[[vocab.ageLabels?.[t.ageBand] || t.ageBand, t.ageBand !== 'both'],
                    [vocab.sexLabels?.[t.sex] || t.sex, t.sex !== 'both'],
                    [vocab.settingLabels?.[t.setting] || t.setting || 'Out-patient', false],
                    //  Both HIGHLIGHTED when present: unlike a 'Both', a service or an
                    //  attendance being set is exactly what distinguishes this template
                    //  from the next, and it changes the claim header when applied.
                    ...(t.attendance
                      ? [[vocab.attendanceLabels?.[t.attendance] || t.attendance, true]] : []),
                    ...(t.service ? [[t.service.code, true]] : []),
                  ].map(([label, strong], i) => (
                      <span key={i} style={{
                        flexShrink: 0, padding: '3px 9px', borderRadius: 999,
                        fontSize: 10.5, fontWeight: strong ? 800 : 650,
                        background: strong ? T.greenSoft : T.head,
                        color: strong ? T.green : T.inkMuted,
                        border: `1px solid ${strong ? `${T.green}33` : T.line}`,
                      }}>{label}</span>
                    ))}

                  <span style={{
                    flexShrink: 0, fontSize: 11.5, color: T.inkMuted, ...MONO,
                    whiteSpace: 'nowrap',
                  }}>
                    {t.items.length} item{t.items.length === 1 ? '' : 's'}
                    {t.source === 'ai' ? ' · AI' : ''}
                  </span>

                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button type="button" onClick={() => editOne(t)} title="Edit it"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '5px 10px', borderRadius: 7, font: 'inherit',
                        fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                        border: `1px solid ${T.line}`, background: '#fff', color: T.inkSoft,
                      }}>
                      <Pencil size={11} /> Edit
                    </button>
                    <button type="button" onClick={() => remove(t)}
                      aria-label={`Delete ${t.name}`} title="Delete it"
                      style={{
                        display: 'inline-flex', alignItems: 'center', padding: '5px 8px',
                        borderRadius: 7, cursor: 'pointer',
                        border: `1px solid ${T.danger}44`, background: T.dangerSoft,
                        color: T.danger,
                      }}>
                      <Trash2 size={12} />
                    </button>
                    <button type="button"
                      onClick={() => setOpen((s) => {
                        const n = new Set(s);
                        if (n.has(t.id)) n.delete(t.id); else n.add(t.id);
                        return n;
                      })}
                      aria-expanded={isOpen}
                      aria-label={isOpen ? 'Hide what it contains' : 'Show what it contains'}
                      style={{
                        display: 'inline-flex', alignItems: 'center', padding: '5px 7px',
                        borderRadius: 7, cursor: 'pointer', border: `1px solid ${T.line}`,
                        background: '#fff', color: T.inkMuted,
                      }}>
                      {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                  </div>
                </div>

                {t.broken && (
                  <div style={{
                    padding: '8px 14px', borderTop: `1px solid ${T.line}`,
                    background: T.dangerSoft, color: T.danger, fontSize: 11.5,
                  }}>
                    This template could not be read in full ({t.broken}). Delete it and
                    build it again.
                  </div>
                )}

                {isOpen && (
                  <div style={{
                    borderTop: `1px solid ${T.line}`, background: T.canvas,
                    padding: '10px 14px 12px', display: 'grid', gap: 10,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  }}>
                    {[['Condition', t.conditions.map((c) => ({
                      code: [c.icd10_code, c.gdrg_code].filter(Boolean).join(' · '),
                      name: c.name,
                    }))],
                    ...KINDS.map((k) => [k.label, t.items.filter((i) => i.kind === k.key)])]
                      .map(([label, rows]) => {
                        if (!rows.length) return null;
                        return (
                          <div key={label} style={{ minWidth: 0 }}>
                            <div style={{
                              fontSize: 9.5, fontWeight: 800, letterSpacing: '.06em',
                              textTransform: 'uppercase', color: T.inkFaint,
                              marginBottom: 4,
                            }}>{label} · {rows.length}</div>
                            <div style={{ display: 'grid', gap: 4 }}>
                              {rows.map((r, i) => (
                                <div key={`${r.code}-${i}`} style={{ minWidth: 0 }}>
                                  <div style={{
                                    fontSize: 12, color: T.ink, overflow: 'hidden',
                                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                  }} title={`${r.code} ${r.name}`}>
                                    <span style={{ ...MONO, fontSize: 11, color: T.inkFaint }}>
                                      {r.code}
                                    </span>{' '}{r.name}
                                  </div>
                                  {/*  A drug with no dose is not a template anybody can
                                       apply, and it is the half most likely to have been
                                       left unfinished. */}
                                  {r.kind === 'medication' && (
                                    <div style={{ fontSize: 11, color: T.inkMuted, lineHeight: 1.5 }}>
                                      {[r.dose, r.frequency,
                                        r.duration ? `${r.duration} day(s)` : null,
                                        r.route, r.quantity ? `qty ${r.quantity}` : null]
                                        .filter(Boolean).join(' · ')
                                        || <span style={{ color: T.danger }}>
                                          no prescription saved
                                        </span>}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/*  What this screen does not do — so nobody hunts for a button that is not here. */}
      {list !== null && (list || []).length > 0 && (
        <div style={{ marginTop: 14, fontSize: 11.5, color: T.inkMuted, lineHeight: 1.7 }}>
          Applying a template needs claims to apply it to, so that stays in{' '}
          <b>Claims → Multi Vet</b>.
        </div>
      )}
    </div>
  );
}
